<template>
  <QuickStatsPageShell
    class="game-detail-shell"
    :title="title"
    :description="description"
    :status-message="statusMessage"
    :show-hero="false"
  >
    <div v-if="loading" class="game-detail-loading" role="status" aria-live="polite">
      <div class="loading-topbar">
        <span class="loading-pill"></span>
        <span class="loading-pill loading-pill--wide"></span>
      </div>
      <div class="loading-hero card">
        <div class="loading-team">
          <span></span>
          <i></i>
          <strong></strong>
        </div>
        <div class="loading-center">
          <span></span>
          <b></b>
          <span></span>
        </div>
        <div class="loading-team">
          <span></span>
          <i></i>
          <strong></strong>
        </div>
      </div>
      <div class="loading-card-grid">
        <div v-for="index in 3" :key="index" class="loading-chart-card card">
          <span></span>
          <strong></strong>
          <i></i>
        </div>
      </div>
      <span class="sr-only">Loading game detail.</span>
    </div>
    <div v-else-if="!game" class="state-text">Game preview data was not found for this game.</div>

    <template v-else>
      <div class="detail-top-nav">
        <router-link to="/quick-stats/game-previews" class="back-link page-back-link">Back</router-link>
      </div>

      <article id="game-summary" class="card detail-hero">
        <div class="hero-topline">
          <div class="league-row">
            <QuickStatsLeagueMark :league="game.league" :logo-url="game.leagueLogo" class="league-logo" />
            <span>{{ game.league }}</span>
          </div>
        </div>

        <div class="matchup-board">
          <div class="team-panel">
            <span class="team-side">Away</span>
            <img v-if="game.awayTeamLogo" :src="game.awayTeamLogo" :alt="`${game.awayTeam} logo`" class="team-logo" loading="lazy" />
            <h2>{{ game.awayTeam }}</h2>
          </div>

          <div class="game-center">
            <span class="game-time">{{ formatRoundedDateTime(game.startTime) }}</span>
            <span class="at-symbol">@</span>
            <span class="updated-text">Updated {{ formatDateTime(game.updatedAt) }}</span>
          </div>

          <div class="team-panel team-panel--home">
            <span class="team-side">Home</span>
            <img v-if="game.homeTeamLogo" :src="game.homeTeamLogo" :alt="`${game.homeTeam} logo`" class="team-logo" loading="lazy" />
            <h2>{{ game.homeTeam }}</h2>
          </div>
        </div>

        <div class="line-card-groups" aria-label="Game market snapshot">
          <section
            v-for="group in lineCardGroups"
            :key="group.label"
            class="line-card-group"
            :class="`line-card-group--${group.items.length}`"
            :aria-label="group.label"
          >
            <div class="line-card-group-title">{{ group.label }}</div>
            <div class="line-card-grid">
              <article v-for="item in group.items" :key="item.label" class="line-card">
                <span class="line-card-label">{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
                <small>{{ item.detail }}</small>
              </article>
            </div>
          </section>
        </div>
      </article>

      <nav class="section-tab-nav" aria-label="Game preview sections">
        <button
          v-for="item in sectionTabs"
          :key="item.value"
          type="button"
          :class="{ active: activeSectionTab === item.value }"
          :aria-pressed="activeSectionTab === item.value"
          @click="activeSectionTab = item.value"
        >
          {{ item.label }}
        </button>
      </nav>

      <section v-if="activeSectionTab === 'volume'" id="volume-handle" class="card visual-card volume-handle-card">
          <div class="section-head">
            <span class="eyebrow">Market Activity</span>
            <h3>Prediction Markets</h3>
          </div>

          <div v-if="volumeHandleRows.length === 0" class="state-text">No prediction market volume history is available for this game.</div>
          <div v-else class="volume-handle-grid">
            <article v-for="row in volumeHandleRows" :key="row.key" class="volume-handle-row">
              <div class="volume-line-panel">
                <div class="row-head">
                  <span>{{ row.label }}</span>
                  <div class="volume-total-pills" aria-label="Latest prediction market volumes">
                    <small class="volume-total-pill volume-total-pill--kalshi">{{ row.kalshiLatestLabel }}</small>
                    <small class="volume-total-pill volume-total-pill--polymarket">{{ row.polymarketLatestLabel }}</small>
                  </div>
                </div>
                <QuickStatsLineChart
                  :aria-label="`${row.label} Kalshi and Polymarket volume chart`"
                  :series="row.predictionVolumeSeries"
                  value-mode="currency"
                  :x-min="row.predictionTimelineDomain.min"
                  :x-max="row.predictionTimelineDomain.max"
                />
                <div class="volume-legend">
                  <span><i class="legend-dot legend-dot--kalshi"></i>Kalshi {{ row.kalshiLatestLabel }}</span>
                  <span><i class="legend-dot legend-dot--polymarket"></i>Polymarket {{ row.polymarketLatestLabel }}</span>
                </div>
              </div>
            </article>
          </div>
      </section>

      <section v-if="activeSectionTab === 'volume'" id="draftkings-activity" class="card visual-card volume-handle-card draftkings-activity-card">
          <div class="section-head">
            <span class="eyebrow">Market Activity</span>
            <h3>DraftKings</h3>
          </div>

          <div v-if="volumeHandleRows.length === 0" class="state-text">No DraftKings handle or ticket history is available for this game.</div>
          <div v-else class="draftkings-activity-grid">
            <article v-for="row in volumeHandleRows" :key="`${row.key}-draftkings`" class="draftkings-activity-row">
              <div class="draftkings-line-panel">
                <div class="draftkings-chart-head">
                  <strong>{{ row.label }}</strong>
                  <span>Handle and volume</span>
                </div>

                <div class="draftkings-split-stack">
                  <div v-for="split in row.draftKingsSplitRows" :key="split.label" class="draftkings-split-row">
                    <div class="draftkings-split-row-head">
                      <span class="draftkings-split-metric">{{ split.label }}</span>
                    </div>
                    <div class="draftkings-split-labels">
                      <div class="draftkings-split-side draftkings-split-side--left" :style="split.firstStyle">
                        <span>{{ split.firstLabel }}</span>
                        <strong>{{ split.firstValue }}</strong>
                      </div>
                      <div class="draftkings-split-side draftkings-split-side--right" :style="split.secondStyle">
                        <span>{{ split.secondLabel }}</span>
                        <strong>{{ split.secondValue }}</strong>
                      </div>
                    </div>
                    <div class="draftkings-split-bar" aria-hidden="true">
                      <i
                        class="draftkings-split-segment"
                        :style="{ ...split.firstStyle, width: `${split.firstPct}%` }"
                      ></i>
                      <i
                        class="draftkings-split-segment"
                        :style="{ ...split.secondStyle, width: `${split.secondPct}%` }"
                      ></i>
                    </div>
                  </div>
                </div>

                <div class="draftkings-side-chart-grid">
                  <div
                    v-for="chart in row.draftKingsSideCharts"
                    :key="chart.label"
                    class="draftkings-side-chart-card"
                    :style="chart.style"
                  >
                    <div class="draftkings-side-chart-head">
                      <span>{{ chart.label }}</span>
                      <div class="draftkings-chart-pill-row">
                        <small class="draftkings-chart-pill">Handle ($) {{ chart.handleLabel }}</small>
                        <small class="draftkings-chart-pill">Volume (ticket count) {{ chart.volumeLabel }}</small>
                      </div>
                    </div>
                    <QuickStatsLineChart
                      :aria-label="`${row.label} DraftKings ${chart.label} handle and volume chart`"
                      :series="chart.series"
                      value-mode="percent"
                      :x-min="chart.timelineDomain.min"
                      :x-max="chart.timelineDomain.max"
                      :y-min="chart.percentDomain.min"
                      :y-max="chart.percentDomain.max"
                    />
                  </div>
                </div>

                <div class="draftkings-line-legend">
                  <span
                    v-for="item in row.draftKingsSideLegendItems"
                    :key="item.label"
                    class="draftkings-line-legend-item"
                    :style="item.style"
                  >
                    <i class="draftkings-line-dot"></i>{{ item.label }}
                  </span>
                  <span class="draftkings-line-legend-item draftkings-line-legend-item--metric">
                    <i class="draftkings-line-sample draftkings-line-sample--solid"></i>Handle ($)
                  </span>
                  <span class="draftkings-line-legend-item draftkings-line-legend-item--metric">
                    <i class="draftkings-line-sample draftkings-line-sample--dashed"></i>Volume (ticket count)
                  </span>
                  <span class="draftkings-line-legend-item draftkings-line-legend-item--metric">
                    <i class="draftkings-gap-sample"></i>Handle-volume gap
                  </span>
                </div>
              </div>
            </article>
          </div>
      </section>

      <section v-if="activeSectionTab === 'odds'" id="price-surface" class="card visual-card price-surface-card">
        <div class="section-head">
          <span class="eyebrow">Prediction Market Implied Odds</span>
        </div>

        <div class="surface-grid">
          <article v-for="venue in marketPriceRows" :key="venue.label" class="surface-panel">
            <div class="surface-title">
              <div>
                <h4>{{ venue.label }}</h4>
                <div class="activity-pill-row">
                  <span v-for="pill in venue.activityPills" :key="pill.label" :class="['activity-pill', pill.className]">
                    <b>{{ pill.label }}</b>{{ pill.value }}
                  </span>
                </div>
              </div>
              <small>{{ venue.snapshotLabel }}</small>
            </div>

            <div class="surface-market-list">
              <article v-for="market in venue.markets" :key="market.label" class="surface-market">
                <div class="surface-market-head">
                  <span>{{ market.label }}</span>
                  <div class="activity-pill-row activity-pill-row--compact">
                    <span v-for="pill in market.activityPills" :key="pill.label" :class="['activity-pill', pill.className]">
                      <b>{{ pill.label }}</b>{{ pill.value }}
                    </span>
                  </div>
                </div>

                <div class="surface-market-body">
                  <div :class="['surface-pie-card', market.pie.paletteClass]">
                    <div class="surface-pie-title">
                      <span>Price Split</span>
                      <small>{{ market.pie.splitLabel }}</small>
                    </div>
                    <div class="pie-layout">
                      <div class="pie-side" :style="market.pie.primaryStyle">
                        <span>{{ market.pie.primaryName }}</span>
                        <strong>{{ market.pie.primaryPrice }}</strong>
                        <small>{{ market.pie.primaryAmerican }}</small>
                      </div>
                      <div class="price-pie" :style="{ background: market.pie.background }" aria-hidden="true"></div>
                      <div class="pie-side" :style="market.pie.secondaryStyle">
                        <span>{{ market.pie.secondaryName }}</span>
                        <strong>{{ market.pie.secondaryPrice }}</strong>
                        <small>{{ market.pie.secondaryAmerican }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </div>

            <div class="surface-volume-chart">
              <div class="surface-chart-head">
                <span>Activity Plot</span>
                <small>Vol / OI snapshot</small>
              </div>
              <div v-for="market in venue.markets" :key="`${venue.label}-${market.label}-activity`" class="surface-chart-row">
                <span>{{ market.label }}</span>
                <div class="surface-chart-bars" aria-hidden="true">
                  <i class="surface-chart-bar surface-chart-bar--volume" :style="{ height: `${market.activityPlot.volumePct}%` }"></i>
                  <i class="surface-chart-bar surface-chart-bar--oi" :style="{ height: `${market.activityPlot.oiPct}%` }"></i>
                </div>
                <div class="activity-pill-row activity-pill-row--chart">
                  <span v-for="pill in market.activityPills" :key="pill.label" :class="['activity-pill', pill.className]">
                    <b>{{ pill.label }}</b>{{ pill.value }}
                  </span>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section v-if="activeSectionTab === 'odds' && oddsMovementRows.length > 0" id="odds-movement" class="card visual-card odds-movement-card">
        <div class="section-head">
          <span class="eyebrow">Sportsbook Odds Tracker</span>
        </div>

        <div class="odds-movement-grid">
          <article v-for="row in oddsMovementRows" :key="row.key" class="odds-movement-panel">
            <div class="odds-movement-head">
              <div>
                <span>{{ row.label }}</span>
                <small>{{ row.subtitle }}</small>
              </div>
              <b>{{ row.books.length }} books</b>
            </div>
            <div class="odds-movement-body">
              <div class="odds-movement-book-list" aria-label="Sportsbook opening and current lines">
                <div class="odds-movement-book odds-movement-book--header" aria-hidden="true">
                  <span>Book</span>
                  <strong>Open</strong>
                  <strong>Now</strong>
                  <b>Diff</b>
                </div>
                <div v-for="book in row.books" :key="book.code" class="odds-movement-book">
                  <span><i :style="{ background: book.color }"></i>{{ book.label }}</span>
                  <strong>{{ book.openLabel }}</strong>
                  <strong>{{ book.currentLabel }}</strong>
                  <b :class="book.moveClass">{{ book.moveLabel }}</b>
                </div>
              </div>
              <div class="odds-movement-chart">
                <QuickStatsLineChart
                  :aria-label="`${row.label} sportsbook odds movement chart`"
                  :series="row.series"
                  :value-mode="row.valueMode"
                  :x-min="row.timelineDomain.min"
                  :x-max="row.timelineDomain.max"
                  :y-min="row.valueDomain.min"
                  :y-max="row.valueDomain.max"
                  height="154px"
                />
              </div>
            </div>
          </article>
        </div>
      </section>

      <section v-if="activeSectionTab === 'trends' && showTrendContext" id="market-signals" class="card visual-card signals-card">
        <div class="section-head">
          <span class="eyebrow">Market Signals</span>
        </div>

        <div class="trend-context">
          <div class="trend-context-head">
            <span>Team Trends</span>
            <div class="trend-timeframe-tabs" aria-label="Trend timeframe filter">
              <button
                v-for="timeframe in trendTimeframeOptions"
                :key="timeframe"
                type="button"
                :class="{ active: selectedTrendTimeframe === timeframe }"
                @click="selectedTrendTimeframe = timeframe"
              >
                {{ formatTimeframeLabel(timeframe, game.league) }}
              </button>
            </div>
          </div>

          <div v-if="trendsLoading" class="trend-context-empty">Loading trend context.</div>
          <div v-else-if="teamTrendCards.length === 0 && leagueTrendCards.length === 0" class="trend-context-empty">
            No matching team or league trend rows are available for this game.
          </div>

          <template v-else>
            <div v-if="teamTrendCards.length > 0" class="team-trend-grid">
              <article
                v-for="team in teamTrendCards"
                :key="team.side"
                class="team-trend-card"
                :style="team.style"
              >
                <div class="team-trend-head">
                  <span>{{ team.side }}</span>
                  <strong>{{ team.name }}</strong>
                </div>
                <div class="team-trend-market-grid">
                  <div
                    v-for="market in team.markets"
                    :key="market.market"
                    :class="['team-trend-market', { 'team-trend-market--total': market.market === 'total' }]"
                  >
                    <span>{{ market.label }}</span>
                    <div class="team-trend-stat-row">
                      <strong>{{ market.winRate }}</strong>
                      <b>{{ market.roi }}</b>
                    </div>
                    <small>{{ market.record }} · {{ market.timeframe }}</small>
                  </div>
                </div>
              </article>
            </div>

            <div v-if="leagueTrendCards.length > 0" class="league-trend-context">
              <div class="trend-context-head trend-context-head--sub">
                <span>League Trends</span>
              </div>
              <div class="league-trend-grid">
                <article v-for="market in leagueTrendCards" :key="market.market" class="league-trend-card">
                  <div class="league-trend-card-head">
                    <span>{{ market.label }}</span>
                    <small>{{ market.timeframe }}</small>
                  </div>
                  <div class="league-trend-table-wrap">
                    <table :class="['league-trend-table', { 'league-trend-table--total': market.market === 'total' }]">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Win Rate</th>
                          <th>ROI</th>
                          <th>Record</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in market.rows" :key="row.id">
                          <td>{{ row.category }}</td>
                          <td>{{ row.winRate }}</td>
                          <td>{{ row.roi }}</td>
                          <td>{{ row.record }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </article>
              </div>
            </div>
          </template>
        </div>
      </section>

      <section v-if="activeSectionTab === 'pricing'" id="sportsbook-board" class="card odds-card">
        <div class="section-head">
          <span class="eyebrow">Book Prices</span>
          <h3>Sportsbook Odds Board</h3>
        </div>

        <div v-if="bestBetRows.length > 0" class="best-bet-grid">
          <article v-for="bet in bestBetRows" :key="`${bet.market}-${bet.selection}`" class="best-bet-card">
            <div class="best-bet-topline">
              <span class="best-bet-market">{{ bet.market }}</span>
              <span class="best-badge">Best</span>
            </div>
            <strong>{{ bet.selection }}</strong>
            <div class="best-bet-book">
              <span>{{ bet.book }}</span>
              <b>{{ bet.price }}</b>
            </div>
          </article>
        </div>

        <div class="odds-board-grid">
          <article v-for="board in sportsbookBoards" :key="board.label" class="odds-board">
            <div class="odds-board-head">
              <div>
                <h4>{{ board.label }}</h4>
                <span>{{ board.subtitle }}</span>
              </div>
              <small>{{ board.books.length }} books</small>
            </div>

            <div v-if="board.books.length === 0" class="state-text">{{ board.emptyMessage }}</div>
            <div v-else class="book-matrix">
              <div class="book-matrix-head">
                <span>Book</span>
                <span v-for="option in board.options" :key="option.key">{{ option.shortLabel }}</span>
              </div>

              <article v-for="book in board.books" :key="`${board.label}-${book.code}`" class="book-row">
                <span class="book-name">{{ book.label }}</span>
                <div
                  v-for="cell in book.cells"
                  :key="cell.key"
                  class="odds-cell"
                  :class="{ 'odds-cell--best': cell.isBest }"
                >
                  <div class="odds-cell-top">
                    <span>{{ cell.label }}</span>
                    <strong>{{ cell.price }}</strong>
                  </div>
                  <div class="odds-strength" aria-hidden="true">
                    <span :style="{ width: `${cell.score}%` }"></span>
                  </div>
                </div>
              </article>
            </div>
          </article>
        </div>
      </section>

      <section v-if="activeSectionTab === 'pricing' && predictionPriceRows.length > 0" id="price-history" class="card visual-card prediction-price-section">
        <div class="prediction-price-head">
          <span>Price History</span>
          <small>Mid price by side</small>
        </div>
        <div class="prediction-price-grid">
          <article v-for="row in predictionPriceRows" :key="`price-${row.key}`" class="prediction-price-row">
            <div class="row-head">
              <span>{{ row.label }}</span>
            </div>
            <div class="prediction-price-platform-grid">
              <div
                v-for="platform in row.platformCharts"
                :key="platform.label"
                class="prediction-price-platform"
                :class="platform.className"
              >
                <div class="prediction-price-platform-head">
                  <span>{{ platform.label }}</span>
                  <div class="volume-total-pills" aria-label="Latest prediction market prices">
                    <small
                      v-for="pill in platform.latestPills"
                      :key="pill.label"
                      class="prediction-price-pill"
                      :aria-label="pill.ariaLabel"
                      :style="pill.style"
                    >
                      {{ pill.value }}
                    </small>
                  </div>
                </div>
                <div class="prediction-price-legend" aria-label="Prediction market price legend">
                  <span
                    v-for="item in platform.legendItems"
                    :key="item.label"
                    class="prediction-price-legend-item"
                    :style="item.style"
                  >
                    <i></i>{{ item.label }}
                  </span>
                </div>
                <QuickStatsLineChart
                  :aria-label="`${row.label} ${platform.label} price history chart`"
                  :series="platform.series"
                  value-mode="cents"
                  :x-min="platform.timelineDomain.min"
                  :x-max="platform.timelineDomain.max"
                  :y-min="platform.priceDomain.min"
                  :y-max="platform.priceDomain.max"
                  height="132px"
                />
                <div class="prediction-price-details" aria-label="Prediction market opening and current prices">
                  <div
                    v-for="item in platform.detailItems"
                    :key="`${item.label}-detail`"
                    class="prediction-price-detail"
                    :style="item.style"
                  >
                    <span>{{ item.label }}</span>
                    <small>Open {{ item.openLabel }}</small>
                    <strong>
                      <i
                        class="prediction-price-move-icon"
                        :class="item.moveClass"
                        role="img"
                        :aria-label="item.moveLabel"
                      ></i>
                      {{ item.currentLabel }}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section v-if="activeSectionTab === 'pricing'" id="prediction-comparison" class="card visual-card prediction-compare-card">
        <div class="section-head">
          <span class="eyebrow">Prediction Market Comparison</span>
          <h3>Kalshi vs Polymarket</h3>
        </div>

        <div class="comparison-grid">
          <article v-for="row in predictionMarketComparisonRows" :key="row.key" class="comparison-row">
            <div class="comparison-head">
              <div>
                <h4>{{ row.label }}</h4>
                <span>{{ row.selection }}</span>
              </div>
              <strong :class="['comparison-edge', row.edgeClass]">{{ row.edgeLabel }}</strong>
            </div>
            <div class="comparison-delta">
              <div class="comparison-price-card">
                <span>Kalshi</span>
                <strong>{{ row.kalshiCentsLabel }}</strong>
                <small>{{ row.kalshiAmericanLabel }}</small>
              </div>
              <div :class="['comparison-meter', row.edgeClass]" aria-hidden="true">
                <span class="comparison-meter__line"></span>
                <span class="comparison-meter__zero"></span>
                <span class="comparison-meter__node" :style="{ left: `${row.deltaPosition}%` }"></span>
              </div>
              <div class="comparison-price-card comparison-price-card--poly">
                <span>Polymarket</span>
                <strong>{{ row.polyCentsLabel }}</strong>
                <small>{{ row.polyAmericanLabel }}</small>
              </div>
            </div>
            <p>{{ row.edgeHelper }}</p>
          </article>
        </div>
      </section>

      <p class="cross-link">
        Browse
        <router-link to="/quick-stats/league-trends">League Trends</router-link>
        and
        <router-link to="/quick-stats/team-rankings">Team Rankings</router-link>
        for additional context.
      </p>
    </template>
  </QuickStatsPageShell>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import QuickStatsLeagueMark from '@/components/QuickStatsLeagueMark.vue'
import QuickStatsLineChart from '@/components/QuickStatsLineChart.vue'
import QuickStatsPageShell from '@/components/QuickStatsPageShell.vue'
import { averageAmericanOddsPair, formatCurrency, formatDateTime, formatLine, formatMarketLabel, formatPercent, formatRoundedDateTime, formatTimeframeLabel, oppositeSpread, sortTimeframes } from '@/lib/quickStats/format'
import { getGamePreviews, getLeagueTrends, getTeamTrends } from '@/lib/quickStats/service'
import { defaultDraftKingsColors, loadTeamColorLookup, resolveMatchupColors } from '@/lib/quickStats/teamColors'

const route = useRoute()
const loading = ref(true)
const statusMessage = ref('')
const game = ref(null)
const teamColorLookup = ref(null)
const teamTrendRows = ref([])
const leagueTrendRows = ref([])
const trendsLoading = ref(true)
const selectedTrendTimeframe = ref('Current Season')
const activeSectionTab = ref('trends')

const title = computed(() => {
  if (!game.value) return 'Game Preview Detail'
  return `${game.value.awayTeam} at ${game.value.homeTeam}`
})

const description = computed(() => {
  if (!game.value) return 'Factual game preview details and sportsbook odds from public data.'
  return `${game.value.awayTeam} at ${game.value.homeTeam} game preview detail with sportsbook and prediction market comparisons.`
})

const sectionTabs = [
  { label: 'Trends', value: 'trends' },
  { label: 'Odds', value: 'odds' },
  { label: 'Volume', value: 'volume' },
  { label: 'Pricing', value: 'pricing' }
]

const trendMarkets = ['moneyline', 'spread', 'total']
const trendTimeframes = ['Current Season', '3m', '1m', '7d', '1d']

const lineCardGroups = computed(() => {
  if (!game.value) return []
  const moneyline = averageAmericanPrices(game.value.moneylineBooks, 'awayPrice', 'homePrice')
  const spreadPrices = averageAmericanPrices(game.value.spreadBooks, 'awayPrice', 'homePrice')
  const totalPrices = averageAmericanPrices(game.value.totalBooks, 'overPrice', 'underPrice')
  return [
    {
      label: 'Consensus Lines',
      items: [
        {
          label: 'Spread',
          value: formatCompactSpreadLine(game.value.spread),
          detail: formatPricePair(spreadPrices, 'Away', 'Home')
        },
        {
          label: 'Total',
          value: `O/U ${formatTotalValue(game.value.total)}`,
          detail: formatPricePair(totalPrices, 'Over', 'Under')
        },
        {
          label: 'Moneyline',
          value: moneyline ? `${formatLine(moneyline.away)} / ${formatLine(moneyline.home)}` : 'N/A',
          detail: 'Sportsbook average'
        }
      ]
    },
    {
      label: 'Prediction Market Activity',
      items: [
        {
          label: 'Kalshi Volume',
          value: formatCurrency(game.value.kalshi.allVolumeUsd ?? game.value.kalshi.volumeUsd),
          detail: 'All markets'
        },
        {
          label: 'Polymarket Volume',
          value: formatCurrency(game.value.polymarket.allVolumeUsd ?? game.value.polymarket.volumeUsd),
          detail: 'All markets'
        }
      ]
    }
  ]
})

const volumeHandleRows = computed(() => {
  if (!game.value) return []
  const sideColors = draftKingsSideColors.value

  return [
    createVolumeHandleRow({
      key: 'moneyline',
      label: 'Moneyline',
      firstLabel: game.value.awayTeam,
      secondLabel: game.value.homeTeam,
      firstDisplayLabel: game.value.awayTeam,
      secondDisplayLabel: game.value.homeTeam,
      kalshi: game.value.predictionMarketHistory?.kalshi?.moneyline,
      polymarket: game.value.predictionMarketHistory?.polymarket?.moneyline,
      draftKings: game.value.draftKingsSplits?.moneyline,
      draftKingsHistory: game.value.draftKingsHistory?.moneyline,
      firstKey: 'awayHandle',
      secondKey: 'homeHandle',
      firstVolumeKey: 'awayVolume',
      secondVolumeKey: 'homeVolume',
      firstColor: sideColors.away,
      secondColor: sideColors.home
    }),
    createVolumeHandleRow({
      key: 'spread',
      label: 'Spread',
      firstLabel: formatTeamSpreadLabel(game.value.awayTeam, awaySpreadLine(game.value.spread)),
      secondLabel: formatTeamSpreadLabel(game.value.homeTeam, game.value.spread),
      firstDisplayLabel: formatTeamSpreadLabel(game.value.awayTeam, awaySpreadLine(game.value.spread)),
      secondDisplayLabel: formatTeamSpreadLabel(game.value.homeTeam, game.value.spread),
      kalshi: game.value.predictionMarketHistory?.kalshi?.spread,
      polymarket: game.value.predictionMarketHistory?.polymarket?.spread,
      draftKings: game.value.draftKingsSplits?.spread,
      draftKingsHistory: game.value.draftKingsHistory?.spread,
      firstKey: 'awayHandle',
      secondKey: 'homeHandle',
      firstVolumeKey: 'awayVolume',
      secondVolumeKey: 'homeVolume',
      firstColor: sideColors.away,
      secondColor: sideColors.home
    }),
    createVolumeHandleRow({
      key: 'total',
      label: 'Total',
      firstLabel: `Over ${formatTotalValue(game.value.total)}`,
      secondLabel: `Under ${formatTotalValue(game.value.total)}`,
      firstDisplayLabel: `Over ${formatTotalValue(game.value.total)}`,
      secondDisplayLabel: `Under ${formatTotalValue(game.value.total)}`,
      kalshi: game.value.predictionMarketHistory?.kalshi?.total,
      polymarket: game.value.predictionMarketHistory?.polymarket?.total,
      draftKings: game.value.draftKingsSplits?.total,
      draftKingsHistory: game.value.draftKingsHistory?.total,
      firstKey: 'overHandle',
      secondKey: 'underHandle',
      firstVolumeKey: 'overVolume',
      secondVolumeKey: 'underVolume',
      firstColor: defaultDraftKingsColors.over,
      secondColor: defaultDraftKingsColors.under
    })
  ].filter(Boolean)
})

const predictionPriceRows = computed(() => {
  if (!game.value) return []
  const sideColors = draftKingsSideColors.value

  return [
    createPredictionPriceRow({
      key: 'moneyline',
      label: 'Moneyline',
      sides: [
        { key: 'away', label: game.value.awayTeam, color: sideColors.away },
        { key: 'home', label: game.value.homeTeam, color: sideColors.home }
      ],
      kalshi: game.value.predictionMarketPriceHistory?.kalshi?.moneyline,
      polymarket: game.value.predictionMarketPriceHistory?.polymarket?.moneyline
    }),
    createPredictionPriceRow({
      key: 'spread',
      label: 'Spread',
      sides: [
        { key: 'away', label: formatTeamSpreadLabel(game.value.awayTeam, awaySpreadLine(game.value.spread)), color: sideColors.away },
        { key: 'home', label: formatTeamSpreadLabel(game.value.homeTeam, game.value.spread), color: sideColors.home }
      ],
      kalshi: game.value.predictionMarketPriceHistory?.kalshi?.spread,
      polymarket: game.value.predictionMarketPriceHistory?.polymarket?.spread
    }),
    createPredictionPriceRow({
      key: 'total',
      label: 'Total',
      sides: [
        { key: 'over', label: `O ${formatTotalValue(game.value.total)}`, color: defaultDraftKingsColors.over },
        { key: 'under', label: `U ${formatTotalValue(game.value.total)}`, color: defaultDraftKingsColors.under }
      ],
      kalshi: game.value.predictionMarketPriceHistory?.kalshi?.total,
      polymarket: game.value.predictionMarketPriceHistory?.polymarket?.total
    })
  ].filter(Boolean)
})

const draftKingsSideColors = computed(() => {
  if (!game.value) {
    return {
      away: defaultDraftKingsColors.away,
      home: defaultDraftKingsColors.home
    }
  }

  return resolveMatchupColors(teamColorLookup.value, {
    league: game.value.league,
    awayTeam: game.value.awayTeam,
    homeTeam: game.value.homeTeam
  })
})

const marketPriceRows = computed(() => {
  if (!game.value) return []
  return [
    createMarketPriceRow('Kalshi', game.value.kalshi),
    createMarketPriceRow('Polymarket', game.value.polymarket)
  ]
})

const predictionMarketComparisonRows = computed(() => {
  if (!game.value) return []
  return [
    createPredictionMarketComparison('ml-away', 'Moneyline', game.value.awayTeam, game.value.kalshi.moneyline.awayPrice, game.value.polymarket.moneyline.awayPrice),
    createPredictionMarketComparison('ml-home', 'Moneyline', game.value.homeTeam, game.value.kalshi.moneyline.homePrice, game.value.polymarket.moneyline.homePrice),
    createPredictionMarketComparison('spread-away', 'Spread', formatTeamSpreadLabel(game.value.awayTeam, awaySpreadLine(game.value.spread)), game.value.kalshi.spread.awayPrice, game.value.polymarket.spread.awayPrice),
    createPredictionMarketComparison('spread-home', 'Spread', formatTeamSpreadLabel(game.value.homeTeam, game.value.spread), game.value.kalshi.spread.homePrice, game.value.polymarket.spread.homePrice),
    createPredictionMarketComparison('total-over', 'Total', `O ${formatTotalValue(game.value.total)}`, game.value.kalshi.total.overPrice, game.value.polymarket.total.overPrice),
    createPredictionMarketComparison('total-under', 'Total', `U ${formatTotalValue(game.value.total)}`, game.value.kalshi.total.underPrice, game.value.polymarket.total.underPrice)
  ].filter(Boolean)
})

const teamTrendCards = computed(() => {
  if (!game.value) return []
  const colors = draftKingsSideColors.value

  return [
    createTeamTrendCard('Away', game.value.awayTeam, game.value.league, colors.away),
    createTeamTrendCard('Home', game.value.homeTeam, game.value.league, colors.home)
  ].filter(Boolean)
})

const leagueTrendCards = computed(() => {
  if (!game.value) return []
  const scopedRows = leagueTrendRows.value.filter((row) => trendKey(row.league) === trendKey(game.value.league))

  return trendMarkets
    .map((market) => createLeagueTrendCard(market, scopedRows, game.value.league))
    .filter(Boolean)
})

const showTrendContext = computed(() => {
  return trendsLoading.value || teamTrendCards.value.length > 0 || leagueTrendCards.value.length > 0
})

const oddsMovementRows = computed(() => {
  if (!game.value) return []
  const plotSpreadPrice = usesStaticSpreadLinePricing(game.value.league)

  return [
    createOddsMovementRow({
      key: 'moneyline-away',
      label: 'Away Moneyline',
      subtitle: `${game.value.awayTeam} price by sportsbook`,
      books: game.value.oddsHistory?.moneyline,
      valueField: 'awayPrice',
      valueMode: 'american',
      showBestEndpoint: true
    }),
    createOddsMovementRow({
      key: 'moneyline-home',
      label: 'Home Moneyline',
      subtitle: `${game.value.homeTeam} price by sportsbook`,
      books: game.value.oddsHistory?.moneyline,
      valueField: 'homePrice',
      valueMode: 'american',
      showBestEndpoint: true
    }),
    createOddsMovementRow({
      key: 'spread-away',
      label: 'Away Spread',
      subtitle: plotSpreadPrice
        ? `${game.value.awayTeam} spread price by sportsbook`
        : `${game.value.awayTeam} line by sportsbook`,
      books: game.value.oddsHistory?.spread,
      valueField: plotSpreadPrice ? 'awayPrice' : 'awayLine',
      valueMode: plotSpreadPrice ? 'american' : 'line',
      showBestEndpoint: true
    }),
    createOddsMovementRow({
      key: 'spread-home',
      label: 'Home Spread',
      subtitle: plotSpreadPrice
        ? `${game.value.homeTeam} spread price by sportsbook`
        : `${game.value.homeTeam} line by sportsbook`,
      books: game.value.oddsHistory?.spread,
      valueField: plotSpreadPrice ? 'homePrice' : 'homeLine',
      valueMode: plotSpreadPrice ? 'american' : 'line',
      showBestEndpoint: true
    }),
    createOddsMovementRow({
      key: 'total',
      label: 'Total',
      subtitle: 'Game total line by sportsbook',
      books: game.value.oddsHistory?.total,
      valueField: 'line',
      valueMode: 'line'
    })
  ].filter(Boolean)
})

const trendTimeframeOptions = computed(() => {
  if (!game.value) return trendTimeframes
  const available = new Set()
  const gameLeagueKey = trendKey(game.value.league)
  const gameTeams = [game.value.awayTeam, game.value.homeTeam]

  teamTrendRows.value.forEach((row) => {
    if (trendKey(row.league) !== gameLeagueKey) return
    if (!gameTeams.some((team) => teamTrendMatches(row, team))) return
    if (row.timeframe) available.add(row.timeframe)
  })

  leagueTrendRows.value.forEach((row) => {
    if (trendKey(row.league) !== gameLeagueKey) return
    if (row.timeframe) available.add(row.timeframe)
  })

  const values = Array.from(available)
  return values.length ? sortTimeframes(values, game.value.league) : trendTimeframes
})

const sportsbookBoards = computed(() => {
  if (!game.value) return []
  return [
    createSportsbookBoard({
      label: 'Moneyline',
      subtitle: 'Best outright price by side',
      emptyMessage: 'No sportsbook moneyline values are currently available for this game.',
      rows: game.value.moneylineBooks,
      options: [
        { key: 'awayPrice', label: game.value.awayTeam, shortLabel: 'Away' },
        { key: 'homePrice', label: game.value.homeTeam, shortLabel: 'Home' }
      ]
    }),
    createSportsbookBoard({
      label: 'Spread',
      subtitle: `Line ${formatCompactSpreadLine(game.value.spread)}`,
      emptyMessage: 'No sportsbook spread prices are currently available for this game.',
      rows: game.value.spreadBooks,
      options: [
        {
          key: 'awayPrice',
          label: formatTeamSpreadLabel(game.value.awayTeam, awaySpreadLine(game.value.spread)),
          shortLabel: 'Away',
          cellLabel: (book) => formatSpreadCellLabel('Away', awaySpreadLine(book.homeSpread ?? game.value.spread)),
          bestLabel: (book) => formatTeamSpreadLabel(game.value.awayTeam, awaySpreadLine(book.homeSpread ?? game.value.spread)),
          groupKey: (book) => spreadGroupKey(awaySpreadLine(book.homeSpread ?? game.value.spread))
        },
        {
          key: 'homePrice',
          label: formatTeamSpreadLabel(game.value.homeTeam, game.value.spread),
          shortLabel: 'Home',
          cellLabel: (book) => formatSpreadCellLabel('Home', book.homeSpread ?? game.value.spread),
          bestLabel: (book) => formatTeamSpreadLabel(game.value.homeTeam, book.homeSpread ?? game.value.spread),
          groupKey: (book) => spreadGroupKey(book.homeSpread ?? game.value.spread)
        }
      ]
    }),
    createSportsbookBoard({
      label: 'Total',
      subtitle: `O/U ${formatTotalValue(game.value.total)}`,
      emptyMessage: 'No sportsbook total prices are currently available for this game.',
      rows: game.value.totalBooks,
      options: [
        { key: 'overPrice', label: `O ${formatTotalValue(game.value.total)}`, shortLabel: 'Over' },
        { key: 'underPrice', label: `U ${formatTotalValue(game.value.total)}`, shortLabel: 'Under' }
      ]
    })
  ]
})

const bestBetRows = computed(() => {
  return sportsbookBoards.value.flatMap((board) => {
    return board.bestBets
  })
})

const gameStructuredData = computed(() => {
  if (!game.value) return ''

  const sportsbookMoneyline = averageAmericanPrices(game.value.moneylineBooks, 'awayPrice', 'homePrice')
  const payload = {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    '@id': `${currentPageUrl()}#game-preview`,
    name: `${game.value.awayTeam} at ${game.value.homeTeam}`,
    sport: game.value.league,
    startDate: isoDate(game.value.startTime),
    dateModified: isoDate(game.value.updatedAt),
    eventStatus: 'https://schema.org/EventScheduled',
    awayTeam: {
      '@type': 'SportsTeam',
      name: game.value.awayTeam
    },
    homeTeam: {
      '@type': 'SportsTeam',
      name: game.value.homeTeam
    },
    additionalProperty: [
      propertyValue('Consensus away spread', awaySpreadLine(game.value.spread)),
      propertyValue('Consensus home spread', game.value.spread),
      propertyValue('Consensus total', game.value.total),
      propertyValue('Average sportsbook away moneyline', sportsbookMoneyline?.away),
      propertyValue('Average sportsbook home moneyline', sportsbookMoneyline?.home),
      propertyValue('Kalshi total volume USD', game.value.kalshi.allVolumeUsd ?? game.value.kalshi.volumeUsd, 'USD'),
      propertyValue('Kalshi total open interest USD', game.value.kalshi.allOpenInterestUsd ?? game.value.kalshi.openInterestUsd, 'USD'),
      propertyValue('Polymarket total volume USD', game.value.polymarket.allVolumeUsd ?? game.value.polymarket.volumeUsd, 'USD'),
      propertyValue('Polymarket total open interest USD', game.value.polymarket.allOpenInterestUsd ?? game.value.polymarket.openInterestUsd, 'USD'),
      propertyValue('Kalshi away moneyline price cents', toCentsValue(game.value.kalshi.moneyline.awayPrice), 'cents'),
      propertyValue('Kalshi home moneyline price cents', toCentsValue(game.value.kalshi.moneyline.homePrice), 'cents'),
      propertyValue('Polymarket away moneyline price cents', toCentsValue(game.value.polymarket.moneyline.awayPrice), 'cents'),
      propertyValue('Polymarket home moneyline price cents', toCentsValue(game.value.polymarket.moneyline.homePrice), 'cents')
    ].filter((item) => item.value !== null)
  }

  return JSON.stringify(payload)
})

function averageAmericanPrices(rows = [], awayField, homeField) {
  const prices = averageAmericanOddsPair(rows, awayField, homeField)
  if (!prices) return null
  return { away: prices.first, home: prices.second }
}

function impliedProbability(value, mode = 'american') {
  const number = Number(value)
  if (!Number.isFinite(number)) return null

  if (mode === 'market') {
    if (number >= 0 && number <= 1) return number * 100
    if (number > 1 && number <= 100) return number
  }

  if (number < 0) return (Math.abs(number) / (Math.abs(number) + 100)) * 100
  if (number > 0) return (100 / (number + 100)) * 100
  return null
}

function normalizeTwoWayProbabilities(awayRaw, homeRaw) {
  if (!Number.isFinite(awayRaw) || !Number.isFinite(homeRaw)) return null
  const total = awayRaw + homeRaw
  if (total <= 0) return null
  return {
    away: (awayRaw / total) * 100,
    home: (homeRaw / total) * 100
  }
}

function createVolumeHandleRow({
  key,
  label,
  firstLabel,
  secondLabel,
  firstDisplayLabel = firstLabel,
  secondDisplayLabel = secondLabel,
  kalshi = [],
  polymarket = [],
  draftKings,
  draftKingsHistory = [],
  firstKey,
  secondKey,
  firstVolumeKey,
  secondVolumeKey,
  firstColor = defaultDraftKingsColors.away,
  secondColor = defaultDraftKingsColors.home
}) {
  const kalshiSeries = createVolumeSeries(kalshi)
  const polymarketSeries = createVolumeSeries(polymarket)
  const allValues = [...kalshiSeries, ...polymarketSeries].map((point) => point.value).filter(Number.isFinite)
  const predictionTimelineDomain = createTimelineDomain(kalshiSeries, polymarketSeries)
  const draftKingsTimelineDomain = createTimelineDomain(draftKingsHistory)
  const firstHandle = normalizeShare(draftKings?.[firstKey])
  const secondHandle = normalizeShare(draftKings?.[secondKey])
  const firstVolume = normalizeShare(draftKings?.[firstVolumeKey])
  const secondVolume = normalizeShare(draftKings?.[secondVolumeKey])
  const fallbackSecond = Number.isFinite(firstHandle) ? 1 - firstHandle : null
  const safeSecondHandle = Number.isFinite(secondHandle) ? secondHandle : fallbackSecond
  const primaryHandle = Number.isFinite(firstHandle) ? firstHandle : 0
  const secondaryHandle = Number.isFinite(safeSecondHandle) ? safeSecondHandle : 0
  const primaryVolume = Number.isFinite(firstVolume) ? firstVolume : 0
  const secondaryVolume = Number.isFinite(secondVolume) ? secondVolume : Number.isFinite(firstVolume) ? 1 - firstVolume : 0

  if (allValues.length === 0 && draftKingsHistory.length === 0 && !Number.isFinite(firstHandle) && !Number.isFinite(secondHandle)) return null

  return {
    key,
    label,
    predictionVolumeSeries: [
      { name: 'Kalshi', color: '#22c55e', data: kalshiSeries },
      { name: 'Polymarket', color: '#3b82f6', data: polymarketSeries }
    ],
    kalshiLatestLabel: formatCurrency(getLatestSeriesValue(kalshiSeries)),
    polymarketLatestLabel: formatCurrency(getLatestSeriesValue(polymarketSeries)),
    predictionTimelineDomain,
    draftKingsTimelineDomain,
    draftKingsSplitRows: [
      createDraftKingsSplitRow('Handle ($)', firstDisplayLabel, secondDisplayLabel, primaryHandle, secondaryHandle, firstColor, secondColor),
      createDraftKingsSplitRow('Volume (ticket count)', firstDisplayLabel, secondDisplayLabel, primaryVolume, secondaryVolume, firstColor, secondColor)
    ],
    draftKingsSideCharts: [
      createDraftKingsSideChart(firstDisplayLabel, primaryHandle, primaryVolume, draftKingsHistory, 'firstHandle', 'firstVolume', firstColor),
      createDraftKingsSideChart(secondDisplayLabel, secondaryHandle, secondaryVolume, draftKingsHistory, 'secondHandle', 'secondVolume', secondColor)
    ],
    draftKingsSideLegendItems: [
      {
        label: firstDisplayLabel,
        style: draftKingsColorStyle(firstColor)
      },
      {
        label: secondDisplayLabel,
        style: draftKingsColorStyle(secondColor)
      }
    ]
  }
}

function createVolumeSeries(points = []) {
  return points
    .map((point) => ({
      ts: point.ts,
      value: Number.isFinite(point.volumeUsd) ? point.volumeUsd : null
    }))
    .filter((point) => point.ts && Number.isFinite(point.value))
}

function createPredictionPriceRow({ key, label, sides = [], kalshi, polymarket }) {
  const platformCharts = [
    createPredictionPricePlatformChart('Kalshi', kalshi, sides),
    createPredictionPricePlatformChart('Polymarket', polymarket, sides)
  ].filter((platform) => platform.series.length > 0)

  if (platformCharts.length === 0) return null

  return {
    key,
    label,
    platformCharts
  }
}

function createPredictionPricePlatformChart(label, marketHistory = {}, sides = []) {
  const series = sides
    .map((side) => createPredictionPriceSeries(marketHistory?.[side.key], side.label, side.color))
    .filter((item) => item.data.length > 0)

  return {
    label,
    className: label.toLowerCase() === 'kalshi' ? 'prediction-price-platform--kalshi' : '',
    series,
    timelineDomain: createPriceTimelineDomain(...series.map((item) => item.data)),
    priceDomain: createPriceYAxisDomain(series),
    latestPills: series.map((item) => ({
      label: item.name,
      value: formatCentsValue(getLatestSeriesValue(item.data)),
      ariaLabel: `${item.name} current price ${formatCentsValue(getLatestSeriesValue(item.data))}`,
      style: selectionColorStyle(item.color)
    })),
    legendItems: series.map((item) => ({
      label: item.name,
      style: selectionColorStyle(item.color)
    })),
    detailItems: series.map((item) => {
      const openValue = getFirstSeriesValue(item.data)
      const currentValue = getLatestSeriesValue(item.data)
      const movement = createPriceMovement(openValue, currentValue)

      return {
        label: item.name,
        openLabel: formatCentsValue(openValue),
        currentLabel: formatCentsValue(currentValue),
        moveClass: movement.className,
        moveLabel: movement.label,
        style: selectionColorStyle(item.color)
      }
    })
  }
}

function createPredictionPriceSeries(points = [], label, color) {
  const data = points
    .map((point) => ({
      ts: point.ts,
      value: normalizeMarketPercent(point.price)
    }))
    .filter((point) => point.ts && Number.isFinite(point.value))
  const latestValue = getLatestSeriesValue(data)

  return {
    name: label,
    shortLabel: compactPriceSideLabel(label),
    endpointLabel: formatEndpointPriceLabel(label, latestValue),
    color,
    lineWidth: 2,
    showSymbol: points.length <= 2,
    data
  }
}

function formatEndpointPriceLabel(label = '', value) {
  const sideLabel = compactEndpointSideLabel(label)
  const price = formatCentsValue(value)
  if (!sideLabel) return price
  return `${sideLabel}\n${price}`
}

function compactEndpointSideLabel(label = '') {
  const text = String(label).trim()
  if (!text) return ''
  if (/^O\s+/i.test(text)) return 'Over'
  if (/^U\s+/i.test(text)) return 'Under'
  return text.replace(/\s+[+-]\d+(\.\d+)?$/, '')
}

function compactPriceSideLabel(label = '') {
  const text = String(label)
  if (text.startsWith('Over')) return 'Over'
  if (text.startsWith('Under')) return 'Under'
  return text
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 4)
    .toUpperCase() || text
}

function formatCentsValue(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return 'N/A'
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(number)}c`
}

function getFirstSeriesValue(points = []) {
  return points[0]?.value
}

function createPriceMovement(openValue, currentValue) {
  const open = Number(openValue)
  const current = Number(currentValue)
  if (!Number.isFinite(open) || !Number.isFinite(current)) {
    return {
      className: 'prediction-price-move-icon--flat',
      label: 'Price movement unavailable'
    }
  }

  const difference = current - open
  if (Math.abs(difference) < 0.05) {
    return {
      className: 'prediction-price-move-icon--flat',
      label: 'Price unchanged from open'
    }
  }

  return difference > 0
    ? {
        className: 'prediction-price-move-icon--up',
        label: `Price up ${formatCentsValue(difference)} from open`
      }
    : {
        className: 'prediction-price-move-icon--down',
        label: `Price down ${formatCentsValue(Math.abs(difference))} from open`
      }
}

const oddsMovementPalette = [
  '#60a5fa',
  '#34d399',
  '#f59e0b',
  '#f472b6',
  '#a78bfa',
  '#22d3ee',
  '#fb7185',
  '#84cc16',
  '#f97316',
  '#c084fc',
  '#e2e8f0'
]

function createOddsMovementRow({ key, label, subtitle, books = [], valueField, valueMode, showBestEndpoint = false }) {
  const normalizedBooks = Array.isArray(books) ? books : []
  const bookRows = normalizedBooks
    .map((book, index) => createOddsMovementBook(book, index, valueField, valueMode))
    .filter(Boolean)
  const bestBook = showBestEndpoint ? selectBestOddsMovementBook(bookRows, valueMode) : null

  if (bookRows.length === 0) return null

  return {
    key,
    label,
    subtitle,
    valueMode: valueMode === 'american' ? 'american' : 'number',
    timelineDomain: createPriceTimelineDomain(...bookRows.map((book) => book.data)),
    valueDomain: createOddsMovementYAxisDomain(bookRows, valueMode),
    series: bookRows.map((book) => ({
      name: book.label,
      color: book.color,
      data: book.data,
      lineWidth: book.code === bestBook?.code ? 2.35 : 1.35,
      opacity: book.code === bestBook?.code ? 1 : 0.7,
      emphasisLineWidth: 2.6,
      endpointLabel: book.code === bestBook?.code ? formatBestOddsEndpointLabel(book, valueMode) : undefined,
      endpointLabelFontSize: 10,
      endpointLabelLineHeight: 12,
      z: book.code === bestBook?.code ? 3 : 1
    })),
    books: bookRows
  }
}

function createOddsMovementBook(book, index, valueField, valueMode) {
  const data = (book.points || [])
    .map((point) => ({
      ts: point.ts,
      value: Number(point[valueField])
    }))
    .filter((point) => point.ts && Number.isFinite(point.value))

  if (data.length === 0) return null

  const openValue = data[0].value
  const currentValue = data.at(-1).value
  const movement = currentValue - openValue

  return {
    code: book.code,
    label: book.label,
    color: oddsMovementPalette[index % oddsMovementPalette.length],
    data,
    currentValue,
    openLabel: formatOddsMovementValue(openValue, valueMode),
    currentLabel: formatOddsMovementValue(currentValue, valueMode),
    moveLabel: formatOddsMovementDifference(movement, valueMode),
    moveClass: movement > 0 ? 'odds-movement-book--up' : movement < 0 ? 'odds-movement-book--down' : ''
  }
}

function selectBestOddsMovementBook(bookRows = [], valueMode = 'line') {
  return bookRows
    .filter((book) => Number.isFinite(book.currentValue))
    .slice()
    .sort((a, b) => b.currentValue - a.currentValue)[0] || null
}

function formatBestOddsEndpointLabel(book, valueMode = 'line') {
  if (!book) return ''
  return `${book.label} ${formatOddsMovementValue(book.currentValue, valueMode)}\n(Best)`
}

function createOddsMovementYAxisDomain(bookRows = [], valueMode = 'line') {
  const values = bookRows
    .flatMap((book) => book.data || [])
    .map((point) => Number(point.value))
    .filter(Number.isFinite)

  if (values.length === 0) return { min: null, max: null }

  const low = Math.min(...values)
  const high = Math.max(...values)
  const range = Math.max(high - low, 0)
  const minimumPadding = valueMode === 'american' ? 16 : 0.5
  const padding = Math.max(range * 0.18, minimumPadding)
  const step = valueMode === 'american' ? 5 : 0.5

  return {
    min: Math.floor((low - padding) / step) * step,
    max: Math.ceil((high + padding) / step) * step
  }
}

function formatOddsMovementValue(value, valueMode = 'line') {
  const number = Number(value)
  if (!Number.isFinite(number)) return 'N/A'
  if (valueMode === 'american') return formatLine(Math.round(number))
  return Number.isInteger(number) ? String(number) : String(Number(number.toFixed(1)))
}

function formatOddsMovementDifference(value, valueMode = 'line') {
  const number = Number(value)
  if (!Number.isFinite(number)) return 'N/A'
  if (Math.abs(number) < 0.05) return '0'
  if (valueMode === 'american') return formatLine(Math.round(number))
  const rounded = Number(number.toFixed(1))
  return formatLine(Number.isInteger(rounded) ? rounded : rounded.toFixed(1))
}

function usesStaticSpreadLinePricing(league) {
  const normalized = String(league || '').trim().toUpperCase()
  return normalized === 'MLB' || normalized === 'NHL'
}

function createPriceTimelineDomain(...collections) {
  const timestamps = collections
    .flatMap((collection) => collection || [])
    .map((point) => point?.ts)
    .filter(Boolean)
    .map((value) => new Date(value).getTime())
    .filter(Number.isFinite)
    .sort((a, b) => a - b)

  if (timestamps.length === 0) {
    return { min: '', max: '' }
  }

  const first = timestamps[0]
  const last = Math.min(timestamps.at(-1), Date.now())

  if (last <= first) {
    return {
      min: new Date(first - 10 * 60 * 1000).toISOString(),
      max: new Date(Math.min(first + 10 * 60 * 1000, Date.now())).toISOString()
    }
  }

  const range = last - first
  const padding = Math.min(Math.max(range * 0.025, 60 * 1000), 12 * 60 * 1000)

  return {
    min: new Date(first - padding).toISOString(),
    max: new Date(Math.min(last + padding, Date.now())).toISOString()
  }
}

function createPriceYAxisDomain(series = []) {
  const values = series
    .flatMap((item) => item.data || [])
    .map((point) => Number(point.value))
    .filter(Number.isFinite)

  if (values.length === 0) {
    return { min: null, max: null }
  }

  const low = Math.min(...values)
  const high = Math.max(...values)
  const range = Math.max(high - low, 0)
  const paddedRange = Math.max(range * 1.28, 4)
  const midpoint = (low + high) / 2
  const rawMin = Math.max(0, midpoint - paddedRange / 2)
  const rawMax = Math.min(100, midpoint + paddedRange / 2)

  return {
    min: roundAxisDown(rawMin),
    max: roundAxisUp(rawMax)
  }
}

function roundAxisDown(value) {
  return Math.floor(value * 2) / 2
}

function roundAxisUp(value) {
  return Math.ceil(value * 2) / 2
}

function createPercentYAxisDomain(series = []) {
  const values = series
    .flatMap((item) => item.data || [])
    .map((point) => Number(point.value))
    .filter(Number.isFinite)

  if (values.length === 0) {
    return { min: 0, max: 100 }
  }

  const low = Math.min(...values)
  const high = Math.max(...values)
  const range = Math.max(high - low, 0)
  const paddedRange = Math.max(range * 1.35, 12)
  const midpoint = (low + high) / 2
  const rawMin = Math.max(0, midpoint - paddedRange / 2)
  const rawMax = Math.min(100, midpoint + paddedRange / 2)

  return {
    min: Math.floor(rawMin),
    max: Math.ceil(rawMax)
  }
}

function createTimelineDomain(...collections) {
  const timestamps = collections
    .flatMap((collection) => collection || [])
    .map((point) => point?.ts)
    .filter(Boolean)
    .map((value) => new Date(value).getTime())
    .filter(Number.isFinite)
    .sort((a, b) => a - b)

  if (timestamps.length === 0) {
    return { min: '', max: '' }
  }

  const first = timestamps[0]
  const last = Math.min(timestamps.at(-1), Date.now())
  const range = Math.max(last - first, 30 * 60 * 1000)
  const padding = Math.min(Math.max(range * 0.04, 5 * 60 * 1000), 45 * 60 * 1000)
  const min = new Date(first - padding).toISOString()
  const max = new Date(Math.min(last + padding, Date.now())).toISOString()

  return {
    min,
    max
  }
}

function getLatestSeriesValue(series = []) {
  return series.at(-1)?.value
}

function normalizeShare(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return null
  if (number > 1) return Math.max(0, Math.min(1, number / 100))
  return Math.max(0, Math.min(1, number))
}

function formatShare(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return 'N/A'
  return `${Math.round(number * 100)}%`
}

function createDraftKingsSideChart(label, handleShare, volumeShare, history = [], handleKey, volumeKey, color) {
  const handleData = createDraftKingsShareSeries(history, handleKey)
  const volumeData = createDraftKingsShareSeries(history, volumeKey)

  return {
    label,
    handleLabel: formatShare(handleShare),
    volumeLabel: formatShare(volumeShare),
    style: selectionColorStyle(color),
    timelineDomain: createPriceTimelineDomain(handleData, volumeData),
    percentDomain: createPercentYAxisDomain([{ data: handleData }, { data: volumeData }]),
    series: [
      ...createDraftKingsSharpGapSeries(history, handleKey, volumeKey, color),
      {
        name: 'Handle ($)',
        color,
        data: handleData,
        endpointLabel: createDraftKingsEndpointLabel('Handle', handleData),
        endpointLabelOffset: [0, -8],
        endpointLabelFontSize: 10,
        endpointLabelLineHeight: 10,
        lineWidth: 2.2,
        z: 3
      },
      {
        name: 'Volume (ticket count)',
        color,
        data: volumeData,
        endpointLabel: createDraftKingsEndpointLabel('Volume', volumeData),
        endpointLabelOffset: [0, 8],
        endpointLabelFontSize: 10,
        endpointLabelLineHeight: 10,
        lineType: 'dashed',
        opacity: 0.76,
        lineWidth: 1.9,
        z: 3
      }
    ]
  }
}

function createDraftKingsSharpGapSeries(history = [], handleKey, volumeKey) {
  const handleStack = `sharp-gap-handle-${handleKey}-${volumeKey}`
  const volumeStack = `sharp-gap-volume-${handleKey}-${volumeKey}`
  const handleGapPoints = []
  const volumeGapPoints = []

  history.forEach((point) => {
    const handle = normalizeShare(point[handleKey])
    const volume = normalizeShare(point[volumeKey])
    if (!point.ts || !Number.isFinite(handle) || !Number.isFinite(volume)) return

    const gapPoint = {
      ts: point.ts,
      floor: Math.min(handle, volume) * 100,
      gap: Math.abs(handle - volume) * 100
    }

    if (handle >= volume) {
      handleGapPoints.push(gapPoint)
      volumeGapPoints.push({ ts: point.ts, floor: null, gap: null })
    } else {
      volumeGapPoints.push(gapPoint)
      handleGapPoints.push({ ts: point.ts, floor: null, gap: null })
    }
  })

  return [
    createDraftKingsGapBaseSeries('__handle-gap-base', handleGapPoints, handleStack, '#22c55e'),
    createDraftKingsGapAreaSeries('__Handle higher gap', handleGapPoints, handleStack, '#22c55e', 0.12),
    createDraftKingsGapBaseSeries('__volume-gap-base', volumeGapPoints, volumeStack, '#94a3b8'),
    createDraftKingsGapAreaSeries('__Volume higher gap', volumeGapPoints, volumeStack, '#94a3b8', 0.2)
  ]
}

function createDraftKingsGapBaseSeries(name, points, stack, color) {
  return {
    name,
    color,
    data: points.map((point) => ({ ts: point.ts, value: point.floor })),
    stack,
    lineWidth: 0,
    opacity: 0,
    showSymbol: false,
    silent: true,
    tooltip: false,
    z: 1
  }
}

function createDraftKingsGapAreaSeries(name, points, stack, color, areaOpacity) {
  return {
    name,
    color,
    data: points.map((point) => ({ ts: point.ts, value: point.gap })),
    stack,
    lineWidth: 0,
    opacity: 0,
    showSymbol: false,
    silent: true,
    tooltip: false,
    areaColor: color,
    areaOpacity,
    z: 1
  }
}

function createDraftKingsSplitRow(label, firstLabel, secondLabel, firstShare, secondShare, firstColor, secondColor) {
  const firstValue = Number.isFinite(firstShare) ? firstShare : 0
  const secondValue = Number.isFinite(secondShare) ? secondShare : 0
  const total = firstValue + secondValue
  const firstPct = total > 0 ? (firstValue / total) * 100 : 50
  const secondPct = total > 0 ? 100 - firstPct : 50

  return {
    label,
    firstLabel,
    secondLabel,
    firstValue: formatShare(firstValue),
    secondValue: formatShare(secondValue),
    firstPct: Math.max(0, Math.min(100, Number(firstPct.toFixed(2)))),
    secondPct: Math.max(0, Math.min(100, Number(secondPct.toFixed(2)))),
    firstStyle: selectionColorStyle(firstColor),
    secondStyle: selectionColorStyle(secondColor)
  }
}

function draftKingsColorStyle(color) {
  return selectionColorStyle(color)
}

function selectionColorStyle(color) {
  return {
    '--side-color': color,
    '--side-tint': hexToRgba(color, 0.16),
    '--side-soft': hexToRgba(color, 0.08)
  }
}

function hexToRgba(hex, alpha) {
  const match = /^#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})$/i.exec(hex)
  if (!match) return `rgba(148, 163, 184, ${alpha})`
  const red = Number.parseInt(match[1], 16)
  const green = Number.parseInt(match[2], 16)
  const blue = Number.parseInt(match[3], 16)
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

function createDraftKingsShareSeries(history = [], key) {
  return history
    .map((point) => ({
      ts: point.ts,
      value: Number.isFinite(normalizeShare(point[key])) ? normalizeShare(point[key]) * 100 : null
    }))
    .filter((point) => point.ts && Number.isFinite(point.value))
}

function createDraftKingsEndpointLabel(label, data = []) {
  const latestValue = getLatestSeriesValue(data)
  if (!Number.isFinite(Number(latestValue))) return ''
  return `${label} ${Math.round(latestValue)}%`
}

function createMarketPriceRow(label, venue) {
  const sideColors = draftKingsSideColors.value
  let marketRows = [
    {
      label: 'Moneyline',
      volume: venue.volumeUsd,
      oi: venue.oiUsd,
      selections: [
        createPriceSelection(game.value.awayTeam, venue.moneyline.awayPrice, sideColors.away),
        createPriceSelection(game.value.homeTeam, venue.moneyline.homePrice, sideColors.home)
      ]
    },
    {
      label: 'Spread',
      volume: venue.spreadVolumeUsd,
      oi: venue.spreadOiUsd,
      selections: [
        createPriceSelection(formatTeamSpreadLabel(game.value.awayTeam, awaySpreadLine(game.value.spread)), venue.spread.awayPrice, sideColors.away),
        createPriceSelection(formatTeamSpreadLabel(game.value.homeTeam, game.value.spread), venue.spread.homePrice, sideColors.home)
      ]
    },
    {
      label: 'Total',
      volume: venue.totalVolumeUsd,
      oi: venue.totalOiUsd,
      selections: [
        createPriceSelection(`O ${formatTotalValue(game.value.total)}`, venue.total.overPrice, defaultDraftKingsColors.over),
        createPriceSelection(`U ${formatTotalValue(game.value.total)}`, venue.total.underPrice, defaultDraftKingsColors.under)
      ]
    }
  ]
  const maxActivity = Math.max(...marketRows.flatMap((market) => [market.volume, market.oi]).filter(Number.isFinite), 1)

  marketRows = marketRows.map((market) => ({
    ...market,
    volumeLabel: formatMarketActivity(market.volume, market.oi),
    activityPills: createActivityPills(market.volume, market.oi),
    activity: createActivityBars(market.volume, market.oi),
    activityPlot: createActivityPlot(market.volume, market.oi, maxActivity),
    pie: createPricePie(market.selections, label)
  }))

  return {
    label,
    activityLabel: formatMarketActivity(venue.allVolumeUsd ?? venue.volumeUsd, venue.allOiUsd ?? venue.oiUsd),
    activityPills: createActivityPills(venue.allVolumeUsd ?? venue.volumeUsd, venue.allOiUsd ?? venue.oiUsd),
    snapshotLabel: game.value.updatedAt ? `Snapshot ${formatDateTime(game.value.updatedAt)}` : 'Latest snapshot',
    markets: marketRows
  }
}

function createPriceSelection(label, value, color) {
  const percent = normalizeMarketPercent(value)
  const centsLabel = formatPriceForMode(value, 'market')
  const americanLabel = formatMarketAmericanLabel(value)
  return {
    label,
    percent,
    centsLabel,
    exactCentsLabel: formatExactCentsForMode(value),
    americanLabel,
    color,
    style: selectionColorStyle(color),
    tooltip: `${label}: ${centsLabel}. Equivalent sportsbook price: ${americanLabel}.`
  }
}

function createPricePie(selections = [], venueLabel = '') {
  const [primary, secondary] = selections
  const primaryValue = Number.isFinite(primary?.percent) ? primary.percent : 0
  const secondaryValue = Number.isFinite(secondary?.percent) ? secondary.percent : 0
  const total = primaryValue + secondaryValue
  const primaryShare = total > 0 ? Number(((primaryValue / total) * 100).toFixed(1)) : 50
  const safeShare = Math.max(0, Math.min(100, primaryShare))
  const palette = predictionMarketPiePalette(venueLabel)
  const primaryColor = primary?.color || palette.primary
  const secondaryColor = secondary?.color || palette.secondary
  const primaryStartAngle = 270 - (safeShare * 3.6) / 2

  return {
    background: `conic-gradient(from ${primaryStartAngle}deg, ${primaryColor} 0 ${safeShare}%, ${secondaryColor} ${safeShare}% 100%)`,
    splitLabel: `${primary?.exactCentsLabel || 'N/A'} / ${secondary?.exactCentsLabel || 'N/A'}`,
    paletteClass: palette.className,
    primaryName: primary?.label || 'Side A',
    primaryPrice: primary?.centsLabel || 'N/A',
    primaryAmerican: primary?.americanLabel || 'N/A',
    primaryStyle: primary?.style || selectionColorStyle(palette.primary),
    secondaryName: secondary?.label || 'Side B',
    secondaryPrice: secondary?.centsLabel || 'N/A',
    secondaryAmerican: secondary?.americanLabel || 'N/A',
    secondaryStyle: secondary?.style || selectionColorStyle(palette.secondary),
    primaryLabel: `${primary?.label || 'Side A'} ${primary?.centsLabel || 'N/A'}`,
    secondaryLabel: `${secondary?.label || 'Side B'} ${secondary?.centsLabel || 'N/A'}`
  }
}

function predictionMarketPiePalette(venueLabel = '') {
  const normalized = venueLabel.toLowerCase()
  if (normalized.includes('poly')) {
    return {
      primary: '#2563eb',
      secondary: '#94a3b8',
      className: 'surface-pie-card--polymarket'
    }
  }
  return {
    primary: '#059669',
    secondary: '#94a3b8',
    className: 'surface-pie-card--kalshi'
  }
}

function createPredictionMarketComparison(key, label, selection, kalshiValue, polyValue) {
  const kalshiPercent = normalizeMarketPercent(kalshiValue)
  const polyPercent = normalizeMarketPercent(polyValue)
  if (kalshiPercent === null && polyPercent === null) return null
  const diff = Number.isFinite(kalshiPercent) && Number.isFinite(polyPercent) ? kalshiPercent - polyPercent : null
  const edge = formatComparisonEdge(diff)
  return {
    key,
    label,
    selection,
    kalshiCentsLabel: formatPriceForMode(kalshiValue, 'market'),
    kalshiAmericanLabel: formatMarketAmericanLabel(kalshiValue),
    polyCentsLabel: formatPriceForMode(polyValue, 'market'),
    polyAmericanLabel: formatMarketAmericanLabel(polyValue),
    edgeLabel: edge.label,
    edgeClass: edge.className,
    edgeHelper: edge.helper,
    deltaPosition: diff === null ? 50 : Math.max(8, Math.min(92, Math.round(50 + diff * 2.4)))
  }
}

function createActivityBars(volume, oi) {
  const volumeValue = Number.isFinite(volume) ? volume : 0
  const oiValue = Number.isFinite(oi) ? oi : 0
  const hasData = volumeValue > 0 || oiValue > 0
  const maxValue = Math.max(volumeValue, oiValue, 1)
  return {
    hasData,
    volumePct: hasData ? Math.max(6, Math.round((volumeValue / maxValue) * 100)) : 0,
    oiPct: hasData ? Math.max(6, Math.round((oiValue / maxValue) * 100)) : 0
  }
}

function createActivityPills(volume, oi) {
  const pills = []
  if (Number.isFinite(volume)) {
    pills.push({ label: 'Vol', value: formatCurrency(volume), className: 'activity-pill--volume' })
  }
  if (Number.isFinite(oi)) {
    pills.push({ label: 'OI', value: formatCurrency(oi), className: 'activity-pill--oi' })
  }
  return pills.length ? pills : [{ label: 'Vol', value: 'N/A', className: 'activity-pill--empty' }]
}

function createActivityPlot(volume, oi, maxActivity) {
  const volumeValue = Number.isFinite(volume) ? volume : 0
  const oiValue = Number.isFinite(oi) ? oi : 0
  const maxValue = Math.max(maxActivity, 1)
  return {
    volumePct: volumeValue > 0 ? Math.max(8, Math.round((volumeValue / maxValue) * 100)) : 0,
    oiPct: oiValue > 0 ? Math.max(8, Math.round((oiValue / maxValue) * 100)) : 0
  }
}

function formatComparisonEdge(diff) {
  if (!Number.isFinite(diff) || Math.abs(diff) < 0.05) {
    return {
      label: '--',
      className: 'comparison-edge--neutral',
      helper: 'No pricing gap between markets.'
    }
  }

  const gap = `${Math.abs(diff).toFixed(1)}c`
  if (diff < 0) {
    return {
      label: `Kalshi -${gap}`,
      className: 'comparison-edge--good',
      helper: `Kalshi is cheaper by ${gap} for this side.`
    }
  }

  return {
    label: `Kalshi +${gap}`,
    className: 'comparison-edge--bad',
    helper: `Kalshi is richer by ${gap}; Polymarket is the cheaper side.`
  }
}

function normalizeMarketPercent(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return null
  if (number >= 0 && number <= 1) return Number((number * 100).toFixed(1))
  if (number > 1 && number <= 100) return Number(number.toFixed(1))
  return null
}

function formatMarketActivity(volume, oi) {
  const hasVolume = Number.isFinite(volume)
  const hasOi = Number.isFinite(oi)
  if (!hasVolume && !hasOi) return 'No volume data'
  if (hasVolume && hasOi) return `Vol ${formatCurrency(volume)} · OI ${formatCurrency(oi)}`
  if (hasVolume) return `Vol ${formatCurrency(volume)}`
  return `OI ${formatCurrency(oi)}`
}

function marketAmericanOdds(value) {
  const percent = normalizeMarketPercent(value)
  return americanOddsFromProbabilityPercent(percent)
}

function americanOddsFromProbabilityPercent(percent) {
  const number = Number(percent)
  if (!Number.isFinite(number) || number <= 0 || number >= 100) return null
  if (number === 50) return 100
  const probability = number / 100
  if (probability > 0.5) return Math.round(-(probability / (1 - probability)) * 100)
  return Math.round(((1 - probability) / probability) * 100)
}

function formatMarketAmericanLabel(value) {
  const odds = marketAmericanOdds(value)
  if (!Number.isFinite(odds)) return 'N/A'
  if (odds === 100) return 'EVEN'
  return formatLine(odds)
}

function createTeamTrendCard(side, teamName, league, color) {
  const rows = teamTrendRows.value.filter((row) => {
    return teamTrendMatches(row, teamName) && trendKey(row.league) === trendKey(league)
  })

  const markets = trendMarkets
    .map((market) => {
      const row = selectPreferredTrendRow(rows.filter((item) => item.market === market), selectedTrendTimeframe.value)
      return createTrendSummary(market, row, league)
    })
    .filter(Boolean)

  if (markets.length === 0) return null

  return {
    side,
    name: teamName,
    style: selectionColorStyle(color),
    markets
  }
}

function createLeagueTrendCard(market, rows, league) {
  const marketRows = rows.filter((row) => row.market === market)
  const preferredTimeframe = selectPreferredTimeframe(marketRows, selectedTrendTimeframe.value)
  if (!preferredTimeframe) return null

  const scopedRows = marketRows
    .filter((row) => row.timeframe === preferredTimeframe)
    .slice()
    .sort((a, b) => {
      const aRate = Number.isFinite(a.winRate) ? a.winRate : -1
      const bRate = Number.isFinite(b.winRate) ? b.winRate : -1
      if (aRate !== bRate) return bRate - aRate
      return String(a.categoryLabel).localeCompare(String(b.categoryLabel))
    })

  if (scopedRows.length === 0) return null

  return {
    market,
    label: formatMarketLabel(market),
    timeframe: formatTimeframeLabel(preferredTimeframe, league),
    rows: scopedRows.map((row) => ({
      id: row.id,
      category: formatLeagueTrendCategory(row),
      record: row.record || 'N/A',
      roi: formatTrendRoiValue(row.roi),
      winRate: formatPercent(row.winRate)
    }))
  }
}

function createTrendSummary(market, row, league) {
  if (!row) return null
  return {
    market,
    label: formatTrendMarketLabel(market, row),
    record: row.record || 'N/A',
    roi: formatTrendRoi(row.roi),
    timeframe: formatTimeframeLabel(row.timeframe, league),
    winRate: formatPercent(row.winRate)
  }
}

function formatTrendMarketLabel(market, row) {
  if (market !== 'total') return formatMarketLabel(market)
  if (row?.side === 'Over' || row?.side === 'Under') return `Total ${row.side}`
  return 'Total Side N/A'
}

function formatLeagueTrendCategory(row) {
  if (row.market === 'total' && (row.side === 'Over' || row.side === 'Under')) {
    return row.side === 'Over' ? 'Over (O)' : 'Under (U)'
  }
  return row.categoryLabel
}

function formatTrendRoi(value) {
  if (value === null || value === undefined || value === '') return 'ROI N/A'
  return `ROI ${formatPercent(value)}`
}

function formatTrendRoiValue(value) {
  if (value === null || value === undefined || value === '') return 'N/A'
  return formatPercent(value)
}

function selectPreferredTrendRow(rows = [], preferred = '') {
  const timeframe = selectPreferredTimeframe(rows, preferred)
  if (!timeframe) return null
  return rows
    .filter((row) => row.timeframe === timeframe)
    .slice()
    .sort((a, b) => {
      const aUpdated = a.updatedAt || ''
      const bUpdated = b.updatedAt || ''
      return bUpdated.localeCompare(aUpdated)
    })[0] || null
}

function selectPreferredTimeframe(rows = [], preferred = '') {
  const available = new Set(rows.map((row) => row.timeframe).filter(Boolean))
  if (preferred) return available.has(preferred) ? preferred : ''
  return trendTimeframes.find((timeframe) => available.has(timeframe)) || Array.from(available)[0] || ''
}

function trendKey(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '')
}

function teamTrendMatches(row, teamName) {
  const target = trendKey(teamName)
  const candidates = [row.entityName, row.teamName].map(trendKey).filter(Boolean)
  if (!target || candidates.length === 0) return false

  return candidates.some((candidate) => {
    if (candidate === target) return true
    const shorter = candidate.length < target.length ? candidate : target
    const longer = candidate.length < target.length ? target : candidate
    return shorter.length >= 5 && longer.includes(shorter)
  })
}

function createSportsbookBoard({ label, subtitle, emptyMessage, rows = [], options = [] }) {
  const bestByGroup = options.reduce((acc, option) => {
    acc[option.key] = createBestBookMap(rows, option)
    return acc
  }, {})

  const books = rows.map((book) => ({
    ...book,
    cells: options.map((option) => {
      const price = book[option.key]
      const group = getOptionGroupKey(option, book)
      const comparableRows = option.groupKey ? rows.filter((row) => getOptionGroupKey(option, row) === group) : rows
      const prices = comparableRows.map((row) => row[option.key]).filter(Number.isFinite)
      const best = bestByGroup[option.key].get(group)
      return {
        key: option.key,
        label: option.cellLabel ? option.cellLabel(book) : option.shortLabel,
        price: formatLine(price),
        score: normalizeBookPriceScore(price, prices),
        isBest: Boolean(best && best.code === book.code)
      }
    })
  }))

  return {
    label,
    subtitle,
    emptyMessage,
    options,
    bestBets: createBestBetRows(label, options, bestByGroup),
    books
  }
}

function createBestBookMap(rows = [], option) {
  const groups = new Map()
  rows
    .filter((row) => Number.isFinite(row[option.key]))
    .forEach((row) => {
      const group = getOptionGroupKey(option, row)
      if (!groups.has(group)) groups.set(group, [])
      groups.get(group).push(row)
    })

  return new Map(Array.from(groups, ([group, groupRows]) => [group, findBestBook(groupRows, option.key)]))
}

function createBestBetRows(market, options, bestByGroup) {
  return options.flatMap((option) => {
    return Array.from(bestByGroup[option.key].entries())
      .map(([, best]) => {
        if (!best) return null
        return {
          market,
          selection: option.bestLabel ? option.bestLabel(best) : option.label,
          book: best.label,
          price: formatLine(best[option.key])
        }
      })
      .filter(Boolean)
  })
}

function getOptionGroupKey(option, row) {
  return option.groupKey ? option.groupKey(row) : 'default'
}

function findBestBook(rows = [], key) {
  return rows
    .filter((row) => Number.isFinite(row[key]))
    .sort((a, b) => {
      if (b[key] !== a[key]) return b[key] - a[key]
      return a.label.localeCompare(b.label)
    })[0] || null
}

function normalizeBookPriceScore(value, prices = []) {
  const number = Number(value)
  if (!Number.isFinite(number) || prices.length === 0) return 0
  const min = Math.min(...prices)
  const max = Math.max(...prices)
  if (min === max) return 72
  return Math.round(18 + ((number - min) / (max - min)) * 82)
}

function formatPriceForMode(value, mode) {
  const number = Number(value)
  if (!Number.isFinite(number)) return 'N/A'
  if (mode === 'market') {
    if (number >= 0 && number <= 1) return formatCentsPrice(number * 100)
    if (number > 1 && number <= 100) return formatCentsPrice(number)
  }
  return formatLine(number)
}

function formatCentsPrice(value) {
  const cents = Number(value)
  if (!Number.isFinite(cents)) return 'N/A'
  const rounded = Number(cents.toFixed(1))
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}c`
}

function formatExactCentsForMode(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return 'N/A'
  if (number >= 0 && number <= 1) return formatTrimmedCents(number * 100)
  if (number > 1 && number <= 100) return formatTrimmedCents(number)
  return formatLine(number)
}

function formatTrimmedCents(value) {
  const cents = Number(value)
  if (!Number.isFinite(cents)) return 'N/A'
  return `${cents.toFixed(3).replace(/\.?0+$/, '')}c`
}

function formatCompactSpreadLine(spread) {
  if (spread === null) return 'N/A'
  return `${formatLine(awaySpreadLine(spread))} / ${formatLine(spread)}`
}

function formatPricePair(prices, firstLabel, secondLabel) {
  if (!prices) return 'N/A'
  return `${firstLabel} ${formatLine(prices.away)} / ${secondLabel} ${formatLine(prices.home)}`
}

function awaySpreadLine(homeSpread) {
  return oppositeSpread(homeSpread)
}

function formatTeamSpreadLabel(team, line) {
  return `${team} ${line === null ? 'N/A' : formatLine(line)}`
}

function formatSpreadCellLabel(side, line) {
  return `${side} ${line === null ? 'N/A' : formatLine(line)}`
}

function spreadGroupKey(line) {
  return line === null ? 'no-line' : String(line)
}

function formatTotalValue(value) {
  if (value === null || value === undefined || value === '') return 'N/A'
  const number = Number(value)
  if (!Number.isFinite(number)) return 'N/A'
  return Number.isInteger(number) ? String(number) : String(Number(number.toFixed(1)))
}

function propertyValue(name, value, unitText) {
  const number = Number(value)
  return {
    '@type': 'PropertyValue',
    name,
    value: Number.isFinite(number) ? number : null,
    ...(unitText ? { unitText } : {})
  }
}

function toCentsValue(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return null
  if (number >= 0 && number <= 1) return Number((number * 100).toFixed(3))
  if (number > 1 && number <= 100) return Number(number.toFixed(3))
  return null
}

function isoDate(value) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

function currentPageUrl() {
  if (typeof window === 'undefined') return ''
  return window.location.href.split('#')[0]
}

function syncGameStructuredData(serialized) {
  if (typeof document === 'undefined') return
  const scriptId = 'paulie-game-preview-jsonld'
  const existing = document.getElementById(scriptId)

  if (!serialized) {
    existing?.remove()
    return
  }

  const script = existing || document.createElement('script')
  script.id = scriptId
  script.type = 'application/ld+json'
  script.textContent = serialized

  if (!existing) {
    document.head.appendChild(script)
  }
}

watch(
  [title, description],
  ([nextTitle, nextDescription]) => {
    document.title = `${nextTitle} - Paulie`
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', nextDescription)
    }
  },
  { immediate: true }
)

watch(gameStructuredData, syncGameStructuredData, { immediate: true })

watch(
  trendTimeframeOptions,
  (options) => {
    if (!options.length) return
    if (!options.includes(selectedTrendTimeframe.value)) {
      selectedTrendTimeframe.value = options[0]
    }
  },
  { immediate: true }
)

onMounted(async () => {
  const [result, lookup, teamTrendResult, leagueTrendResult] = await Promise.all([
    getGamePreviews(),
    loadTeamColorLookup(),
    getTeamTrends(),
    getLeagueTrends()
  ])
  teamColorLookup.value = lookup
  teamTrendRows.value = teamTrendResult.rows
  leagueTrendRows.value = leagueTrendResult.rows
  statusMessage.value = [result.errorMessage, teamTrendResult.errorMessage, leagueTrendResult.errorMessage].filter(Boolean).join(' ')
  game.value = result.rows.find((row) => row.gameSlug === route.params.gameSlug) || null
  trendsLoading.value = false
  loading.value = false
})

onUnmounted(() => {
  syncGameStructuredData('')
})
</script>

<style scoped>
:deep(.game-detail-shell) {
  --detail-bg: #f6f7fb;
  --detail-panel: #0f172a;
  --detail-panel-strong: #07111f;
  --detail-panel-soft: #111d33;
  --detail-border: rgba(148, 163, 184, 0.24);
  --detail-border-strong: rgba(191, 219, 254, 0.34);
  --detail-ink: #f8fbff;
  --detail-ink-soft: #dbeafe;
  --detail-muted: #9fb0c8;
  --detail-muted-strong: #64748b;
  background:
    linear-gradient(180deg, #ffffff 0, #f7f8fc 220px, var(--detail-bg) 100%);
}

:deep(.game-detail-shell .qs-content) {
  padding-bottom: 4.25rem;
}

.state-text {
  color: var(--text-muted);
}

:deep(.game-detail-shell .qs-content > .container) {
  --detail-bg: #f6f7fb;
  --detail-panel: #0f172a;
  --detail-panel-strong: #07111f;
  --detail-panel-soft: #111d33;
  --detail-border: rgba(148, 163, 184, 0.24);
  --detail-border-strong: rgba(191, 219, 254, 0.34);
  --detail-ink: #f8fbff;
  --detail-ink-soft: #dbeafe;
  --detail-muted: #9fb0c8;
  --detail-muted-strong: #64748b;
  max-width: 1360px;
}

.game-detail-loading {
  display: grid;
  gap: 0.75rem;
}

.loading-topbar,
.loading-hero,
.loading-card-grid {
  min-width: 0;
}

.loading-topbar {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.loading-pill,
.loading-team span,
.loading-team i,
.loading-team strong,
.loading-center span,
.loading-center b,
.loading-chart-card span,
.loading-chart-card strong,
.loading-chart-card i {
  display: block;
  overflow: hidden;
  border-radius: 999px;
  background:
    linear-gradient(90deg, rgba(225, 225, 218, 0.94), rgba(91, 124, 255, 0.18), rgba(225, 225, 218, 0.94)),
    #eeeeea;
  background-size: 220% 100%;
  box-shadow: inset 0 0 0 1px rgba(207, 207, 199, 0.55);
  animation: loading-sheen 1.3s ease-in-out infinite;
}

.loading-pill {
  width: 58px;
  height: 30px;
}

.loading-pill--wide {
  width: min(520px, 72vw);
}

.loading-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(96px, 0.34fr) minmax(0, 1fr);
  align-items: center;
  gap: 0.8rem;
  border: 1px solid rgba(207, 207, 199, 0.92);
  background: linear-gradient(180deg, #ffffff, #fbfbf7);
  padding: 1rem;
}

.loading-team,
.loading-center,
.loading-chart-card {
  display: grid;
  justify-items: center;
  gap: 0.55rem;
  min-width: 0;
}

.loading-team {
  min-height: 148px;
  border: 1px solid rgba(207, 207, 199, 0.86);
  border-radius: 8px;
  background: #f8f8f4;
  padding: 0.9rem;
}

.loading-team span {
  width: 44px;
  height: 10px;
}

.loading-team i {
  width: 52px;
  height: 52px;
}

.loading-team strong {
  width: min(190px, 80%);
  height: 18px;
}

.loading-center span {
  width: 108px;
  height: 12px;
}

.loading-center b {
  width: 28px;
  height: 28px;
}

.loading-card-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.loading-chart-card {
  min-height: 128px;
  border: 1px solid rgba(207, 207, 199, 0.86);
  background: #f8f8f4;
  padding: 0.8rem;
}

.loading-chart-card span {
  justify-self: start;
  width: 82px;
  height: 12px;
}

.loading-chart-card strong {
  width: 78%;
  height: 58px;
  border-radius: 8px;
}

.loading-chart-card i {
  width: 52%;
  height: 10px;
}

@keyframes loading-sheen {
  0% {
    background-position: 120% 0;
  }

  100% {
    background-position: -120% 0;
  }
}

.sr-only {
  position: absolute;
  overflow: hidden;
  width: 1px;
  height: 1px;
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

.detail-hero,
.visual-card,
.odds-card {
  --text-primary: #0f172a;
  --text-secondary: #334155;
  --text-muted: #64748b;
  border: 1px solid rgba(203, 213, 225, 0.88);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.98)),
    #ffffff;
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.08);
  color: #0f172a;
  padding: 1rem;
}

.detail-hero :where(h1, h2, h3, h4, strong, b),
.visual-card :where(h1, h2, h3, h4, strong, b),
.odds-card :where(h1, h2, h3, h4, strong, b) {
  color: var(--detail-ink, #f8fbff);
}

.detail-hero :where(span, small, p, td, th),
.visual-card :where(span, small, p, td, th),
.odds-card :where(span, small, p, td, th) {
  color: inherit;
}

.detail-hero {
  overflow: hidden;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.99), rgba(248, 250, 252, 0.98)),
    #ffffff;
}

.hero-topline,
.league-row,
.market-title,
.row-head,
.metric-pair,
.probability-labels {
  display: flex;
  align-items: center;
}

.hero-topline {
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.league-row {
  gap: 0.5rem;
  color: #475569;
  font-weight: 700;
  text-transform: uppercase;
}

.league-logo {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.back-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  border: 1px solid rgba(91, 124, 255, 0.26);
  border-radius: 8px;
  background: #ffffff;
  color: var(--accent);
  font-size: 0.78rem;
  font-weight: 800;
  padding: 0.42rem 0.7rem;
  text-decoration: none;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
}

.detail-top-nav {
  display: flex;
  align-items: center;
  margin-bottom: 0.65rem;
}

.page-back-link {
  margin-bottom: 0;
}

.section-tab-nav {
  display: flex;
  flex-wrap: nowrap;
  margin: 0.75rem 0 1rem;
  overflow-x: auto;
  border: 1px solid rgba(203, 213, 225, 0.72);
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);
  padding: 0.2rem;
  scrollbar-width: none;
}

.section-tab-nav::-webkit-scrollbar {
  display: none;
}

.section-tab-nav button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  flex: 0 0 auto;
  border-right: 1px solid rgba(226, 232, 240, 0.88);
  border-top: 0;
  border-bottom: 0;
  border-left: 0;
  background: transparent;
  color: #526076;
  cursor: pointer;
  font: inherit;
  font-size: 0.72rem;
  font-weight: 850;
  padding: 0.42rem 0.82rem;
  white-space: nowrap;
}

.section-tab-nav button:last-child {
  border-right: 0;
}

.section-tab-nav button::after {
  content: '';
  position: absolute;
  left: 0.72rem;
  right: 0.72rem;
  bottom: 3px;
  height: 2px;
  border-radius: 999px 999px 0 0;
  background: transparent;
}

.section-tab-nav button:hover,
.section-tab-nav button.active {
  border-radius: 6px;
  background: rgba(91, 124, 255, 0.12);
  color: #23345d;
}

.section-tab-nav button:hover::after,
.section-tab-nav button.active::after {
  background: #60a5fa;
}

#market-signals,
#odds-movement,
#volume-handle,
#price-history {
  scroll-margin-top: 96px;
}

.matchup-board {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 1rem;
  align-items: stretch;
}

.team-panel {
  display: grid;
  align-content: center;
  justify-items: center;
  min-height: 124px;
  border: 1px solid rgba(91, 124, 255, 0.32);
  border-radius: 10px;
  background:
    linear-gradient(180deg, rgba(226, 232, 240, 0.98), rgba(203, 213, 225, 0.9)),
    #e2e8f0;
  padding: 0.78rem;
  text-align: center;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.62), 0 10px 26px rgba(15, 23, 42, 0.06);
}

.team-panel--home {
  background:
    linear-gradient(180deg, rgba(224, 231, 255, 0.98), rgba(203, 213, 225, 0.9)),
    #e0e7ff;
  border-color: rgba(91, 124, 255, 0.38);
}

.team-side,
.eyebrow {
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.team-logo {
  width: 46px;
  height: 46px;
  object-fit: contain;
  margin: 0.48rem 0;
  filter: drop-shadow(0 8px 18px rgba(0, 0, 0, 0.22));
}

.team-panel h2 {
  color: #0f172a;
  font-size: clamp(1.05rem, 1.55vw, 1.46rem);
  letter-spacing: 0;
}

.game-center {
  display: grid;
  align-content: center;
  justify-items: center;
  min-width: 132px;
  text-align: center;
}

.game-time,
.updated-text {
  color: #475569;
  font-size: 0.76rem;
}

.at-symbol {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  margin: 0.48rem 0;
  border: 1px solid rgba(91, 124, 255, 0.24);
  border-radius: 999px;
  background: rgba(91, 124, 255, 0.08);
  color: var(--accent);
  font-weight: 900;
}

.line-card-groups {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  gap: 0.72rem;
  margin-top: 0.82rem;
  align-items: start;
}

.line-card-group {
  display: grid;
  gap: 0.44rem;
  min-width: 0;
}

.line-card-group-title {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  border: 1px solid rgba(148, 163, 184, 0.58);
  border-radius: 6px 6px 0 0;
  background: #e2e8f0;
  color: #0f172a;
  font-size: 0.64rem;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: uppercase;
}

.line-card-grid {
  display: grid;
  gap: 0.42rem;
}

.line-card-group--3 .line-card-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.line-card-group--2 .line-card-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.line-card,
.market-panel,
.odds-panel {
  border: 1px solid rgba(148, 163, 184, 0.34);
  border-radius: 8px;
  background: #ffffff;
}

.line-card {
  position: relative;
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 0.16rem;
  min-height: 86px;
  padding: 0;
  text-align: center;
}

.line-card-label,
.market-stat span,
.metric-pair,
.probability-labels,
.row-head small {
  color: #64748b;
}

.line-card-label {
  display: grid;
  align-items: center;
  min-height: 25px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.42);
  background: #e9eef5;
  color: #0f172a;
  font-size: 0.63rem;
  font-weight: 850;
  text-transform: uppercase;
}

.line-card-group:nth-child(2) .line-card:nth-child(1) {
  border-left: 2px solid #22c55e;
}

.line-card-group:nth-child(2) .line-card:nth-child(2) {
  border-left: 2px solid #3b82f6;
}

.line-card strong {
  display: block;
  color: #0f172a;
  font-size: 0.78rem;
  line-height: 1.25;
  min-width: 0;
  overflow: hidden;
  padding: 0.48rem 0.32rem 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.line-card small {
  color: var(--text-muted);
  display: block;
  font-size: 0.56rem;
  line-height: 1.2;
  min-width: 0;
  overflow: hidden;
  padding: 0 0.24rem 0.48rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.section-head {
  border-bottom: 1px solid rgba(148, 163, 184, 0.22);
  margin-bottom: 0.85rem;
  padding-bottom: 0.7rem;
}

.section-head h3 {
  color: #0f172a;
  margin-top: 0.2rem;
  font-size: 1.1rem;
}

.volume-handle-row,
.draftkings-activity-row,
.prediction-price-row,
.prediction-price-platform,
.draftkings-split-stack,
.draftkings-side-chart-card,
.team-trend-card,
.league-trend-card,
.team-trend-market,
.odds-movement-panel,
.odds-movement-chart,
.odds-movement-book,
.surface-panel,
.surface-market,
.surface-pie-card,
.pie-side,
.price-node,
.comparison-row,
.comparison-price-card,
.best-bet-card,
.odds-board,
.book-name,
.odds-cell {
  --text-primary: #f8fbff;
  --text-secondary: #dbeafe;
  --text-muted: rgba(203, 213, 225, 0.78);
}

.volume-handle-card {
  margin-top: 1rem;
}

.volume-handle-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.72rem;
}

.draftkings-activity-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.9rem;
}

.volume-handle-row,
.draftkings-activity-row {
  display: block;
  align-items: stretch;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 10px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(2, 6, 23, 0.88)),
    #07111f;
  padding: 0.88rem;
}

.volume-line-panel,
.draftkings-line-panel {
  min-width: 0;
}

.volume-total-pills {
  display: flex;
  justify-content: flex-end;
  gap: 0.3rem;
  min-width: 0;
}

.volume-total-pill {
  display: inline-flex;
  align-items: center;
  max-width: 104px;
  min-height: 20px;
  overflow: hidden;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 0.56rem;
  font-weight: 850;
  padding: 0.15rem 0.42rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.volume-total-pill--kalshi {
  border-color: rgba(34, 197, 94, 0.42);
  background: rgba(34, 197, 94, 0.12);
  color: #047857;
}

.volume-total-pill--polymarket {
  border-color: rgba(59, 130, 246, 0.42);
  background: rgba(59, 130, 246, 0.12);
  color: #1d4ed8;
}

.volume-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.28rem 0.56rem;
  color: var(--text-muted);
  font-size: 0.6rem;
}

.volume-legend span {
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  min-width: 0;
}

.prediction-price-section {
  display: grid;
  gap: 0.7rem;
  margin-top: 1rem;
  border-top: 1px solid rgba(202, 220, 255, 0.12);
  padding-top: 0.9rem;
}

.prediction-price-head,
.prediction-price-platform-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.7rem;
}

.prediction-price-head,
.draftkings-chart-head,
.trend-context-head,
.surface-title,
.odds-board-head {
  border-bottom: 1px solid rgba(148, 163, 184, 0.22);
  padding-bottom: 0.58rem;
}

.prediction-price-head span {
  color: #f8fbff;
  font-size: 0.78rem;
  font-weight: 950;
  text-transform: uppercase;
}

.prediction-price-head small {
  color: var(--text-muted);
  font-size: 0.68rem;
  font-weight: 850;
}

.prediction-price-grid {
  display: grid;
  gap: 0.72rem;
}

.prediction-price-row {
  display: grid;
  gap: 0.56rem;
  border: 1px solid rgba(202, 220, 255, 0.14);
  border-radius: 9px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.48), rgba(3, 7, 18, 0.24)),
    rgba(8, 13, 27, 0.22);
  padding: 0.66rem;
}

.prediction-price-platform-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.72rem;
}

.prediction-price-platform {
  display: grid;
  gap: 0.46rem;
  min-width: 0;
  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 8px;
  background: rgba(3, 7, 18, 0.22);
  padding: 0.62rem 0.62rem 0.52rem;
}

.prediction-price-platform--kalshi {
  border-color: rgba(34, 197, 94, 0.24);
  background:
    linear-gradient(135deg, rgba(34, 197, 94, 0.12), rgba(3, 7, 18, 0.2) 48%),
    rgba(4, 18, 11, 0.36);
  box-shadow: inset 0 0 0 1px rgba(34, 197, 94, 0.08);
}

.prediction-price-platform-head span {
  color: #f8fbff;
  font-size: 0.66rem;
  font-weight: 900;
  text-transform: uppercase;
}

.prediction-price-pill {
  display: inline-flex;
  align-items: center;
  max-width: 92px;
  min-height: 20px;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--side-color, #94a3b8) 34%, rgba(148, 163, 184, 0.18));
  border-radius: 999px;
  background: var(--side-soft, rgba(148, 163, 184, 0.08));
  color: #f8fbff;
  font-size: 0.56rem;
  font-weight: 850;
  gap: 0.18rem;
  padding: 0.14rem 0.38rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prediction-price-pill b {
  color: var(--side-color, #f8fbff);
  font-weight: 950;
}

.prediction-price-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.36rem 0.52rem;
  min-width: 0;
}

.prediction-price-legend-item {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  color: #f8fbff;
  font-size: 0.62rem;
  font-weight: 850;
  gap: 0.26rem;
  line-height: 1.15;
}

.prediction-price-legend-item i {
  width: 7px;
  height: 7px;
  flex: 0 0 auto;
  border-radius: 999px;
  background: var(--side-color, #94a3b8);
  box-shadow: 0 0 0 3px var(--side-soft, rgba(148, 163, 184, 0.1));
}

.prediction-price-details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.4rem;
}

.prediction-price-detail {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 0.44rem;
  min-width: 0;
  border: 1px solid color-mix(in srgb, var(--side-color, #94a3b8) 22%, rgba(148, 163, 184, 0.12));
  border-radius: 7px;
  background: linear-gradient(135deg, var(--side-soft, rgba(148, 163, 184, 0.08)), rgba(3, 7, 18, 0.2));
  padding: 0.36rem 0.46rem;
}

.prediction-price-detail span {
  min-width: 0;
  overflow: hidden;
  color: #f8fbff;
  font-size: 0.62rem;
  font-weight: 850;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prediction-price-detail small {
  color: rgba(203, 213, 225, 0.78);
  font-size: 0.56rem;
  font-weight: 850;
  text-transform: uppercase;
  white-space: nowrap;
}

.prediction-price-detail strong {
  display: inline-flex;
  align-items: center;
  gap: 0.22rem;
  color: var(--side-color, #f8fbff);
  font-size: 0.68rem;
  font-weight: 950;
  white-space: nowrap;
}

.prediction-price-move-icon {
  position: relative;
  width: 13px;
  height: 13px;
  flex: 0 0 auto;
  border: 1px solid color-mix(in srgb, currentColor 40%, transparent);
  border-radius: 999px;
  background: color-mix(in srgb, currentColor 12%, transparent);
  color: rgba(203, 213, 225, 0.86);
}

.prediction-price-move-icon::before,
.prediction-price-move-icon::after {
  position: absolute;
  content: '';
}

.prediction-price-move-icon--up {
  color: #34d399;
}

.prediction-price-move-icon--down {
  color: #f87171;
}

.prediction-price-move-icon--up::before,
.prediction-price-move-icon--down::before {
  top: 3px;
  left: 4px;
  width: 4px;
  height: 4px;
  border-top: 1.5px solid currentColor;
  border-right: 1.5px solid currentColor;
}

.prediction-price-move-icon--up::before {
  transform: rotate(-45deg);
}

.prediction-price-move-icon--down::before {
  top: 4px;
  transform: rotate(135deg);
}

.prediction-price-move-icon--up::after,
.prediction-price-move-icon--down::after {
  left: 5.5px;
  width: 1.5px;
  height: 5px;
  border-radius: 999px;
  background: currentColor;
}

.prediction-price-move-icon--up::after {
  top: 4px;
}

.prediction-price-move-icon--down::after {
  top: 3px;
}

.prediction-price-move-icon--flat::before {
  top: 5.3px;
  left: 3px;
  width: 6px;
  height: 1.5px;
  border-radius: 999px;
  background: currentColor;
}

.legend-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
}

.legend-dot--kalshi {
  background: #22c55e;
}

.legend-dot--polymarket {
  background: #3b82f6;
}

.draftkings-line-panel {
  display: grid;
  gap: 0.72rem;
}

.draftkings-chart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  color: var(--text-muted);
  font-size: 0.68rem;
}

.draftkings-chart-head strong {
  color: #f8fbff;
  font-size: 0.78rem;
  font-weight: 900;
  text-transform: uppercase;
}

.draftkings-split-stack {
  display: grid;
  gap: 0.58rem;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 9px;
  background: rgba(2, 6, 23, 0.22);
  padding: 0.68rem;
}

.draftkings-split-row {
  display: grid;
  gap: 0.36rem;
  min-width: 0;
}

.draftkings-split-row-head,
.draftkings-split-labels {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
}

.draftkings-split-row-head {
  margin-bottom: -0.1rem;
}

.draftkings-split-metric {
  color: var(--text-muted);
  font-size: 0.62rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.18;
  text-transform: uppercase;
}

.draftkings-split-side {
  display: grid;
  gap: 0.1rem;
  min-width: 0;
  max-width: 46%;
  overflow: hidden;
}

.draftkings-split-side span {
  min-width: 0;
  overflow: hidden;
  color: #f8fbff;
  font-size: 0.64rem;
  font-weight: 900;
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.draftkings-split-side strong {
  color: #f8fbff;
  font-size: clamp(0.98rem, 2vw, 1.32rem);
  font-weight: 950;
  line-height: 1;
  text-shadow: 0 0 16px var(--side-tint, rgba(148, 163, 184, 0.16));
}

.draftkings-split-side--right {
  justify-items: end;
  text-align: right;
}

.draftkings-split-bar {
  display: flex;
  overflow: hidden;
  height: 24px;
  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.54);
  box-shadow: inset 0 0 0 1px rgba(2, 6, 23, 0.34), 0 10px 26px rgba(2, 6, 23, 0.22);
}

.draftkings-split-segment {
  display: block;
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--side-color, #94a3b8) 88%, #fff), var(--side-color, #94a3b8)),
    var(--side-color, #94a3b8);
}

.draftkings-side-chart-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem;
}

.draftkings-side-chart-card {
  display: grid;
  gap: 0.3rem;
  min-width: 0;
  border: 1px solid color-mix(in srgb, var(--side-color, #94a3b8) 38%, rgba(148, 163, 184, 0.24));
  border-radius: 9px;
  background:
    linear-gradient(180deg, var(--side-soft, rgba(148, 163, 184, 0.08)), rgba(3, 7, 18, 0.5)),
    #07111f;
  padding: 0.62rem 0.58rem 0.42rem;
}

.draftkings-side-chart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.55rem;
  min-width: 0;
}

.draftkings-side-chart-head span {
  min-width: 0;
  overflow: hidden;
  color: #f8fbff;
  font-size: 0.7rem;
  font-weight: 900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.draftkings-chart-pill-row {
  display: flex;
  flex: 0 1 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.28rem;
  min-width: 0;
}

.draftkings-chart-pill {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  max-width: 164px;
  min-width: 0;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--side-color, #94a3b8) 34%, rgba(148, 163, 184, 0.18));
  border-radius: 999px;
  background: var(--side-soft, rgba(148, 163, 184, 0.08));
  color: #f8fbff;
  font-size: 0.57rem;
  font-weight: 850;
  line-height: 1.1;
  padding: 0.15rem 0.4rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.draftkings-line-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 0.34rem 0.45rem;
  color: var(--text-muted);
  font-size: 0.62rem;
  font-weight: 800;
}

.draftkings-line-legend-item {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
  gap: 0.26rem;
  border: 1px solid color-mix(in srgb, var(--side-color, #94a3b8) 42%, rgba(148, 163, 184, 0.18));
  border-radius: 999px;
  background: var(--side-soft, rgba(148, 163, 184, 0.08));
  color: #f8fbff;
  line-height: 1.18;
  overflow: hidden;
  padding: 0.18rem 0.42rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.draftkings-line-dot {
  width: 6px;
  height: 6px;
  flex: 0 0 auto;
  border-radius: 999px;
  background: var(--side-color, #94a3b8);
  box-shadow: 0 0 0 2px var(--side-tint, rgba(148, 163, 184, 0.16));
}

.draftkings-line-sample {
  display: inline-block;
  width: 16px;
  height: 0;
  border-top: 2px solid rgba(226, 232, 240, 0.82);
}

.draftkings-line-sample--dashed {
  border-top-style: dashed;
}

.draftkings-gap-sample {
  display: inline-block;
  width: 16px;
  height: 8px;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 3px;
  background: rgba(226, 232, 240, 0.16);
}

.draftkings-line-legend-item--metric {
  background: rgba(148, 163, 184, 0.07);
  border-color: rgba(148, 163, 184, 0.18);
  color: var(--text-muted);
}

.row-head {
  justify-content: space-between;
  gap: 1rem;
}

.row-head span {
  color: #eef3ff;
  font-weight: 800;
}

.metric-bar {
  position: relative;
  overflow: hidden;
  height: 14px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.18);
}

.metric-bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #2dd4bf, #60a5fa);
}

.metric-pair {
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.78rem;
}

.market-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.signals-card {
  margin-top: 1rem;
}

.trend-context {
  display: grid;
  gap: 0.7rem;
}

.trend-context-head,
.team-trend-head,
.league-trend-card-head,
.league-trend-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.7rem;
}

.trend-context-head span {
  color: #f8fbff;
  font-size: 0.78rem;
  font-weight: 950;
  text-transform: uppercase;
}

.trend-timeframe-tabs {
  display: inline-flex;
  overflow: hidden;
  border: 1px solid rgba(202, 220, 255, 0.16);
  border-radius: 999px;
  background: rgba(2, 6, 23, 0.36);
  padding: 0.15rem;
}

.trend-timeframe-tabs button {
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  font: inherit;
  font-size: 0.6rem;
  font-weight: 900;
  line-height: 1;
  min-height: 24px;
  padding: 0.28rem 0.46rem;
  text-transform: uppercase;
}

.trend-timeframe-tabs button.active {
  background: var(--accent);
  color: #ffffff;
}

.trend-context-head small,
.trend-context-empty {
  color: var(--text-muted);
  font-size: 0.68rem;
  font-weight: 850;
}

.trend-context-head--sub {
  margin-top: 0.1rem;
}

.team-trend-grid,
.league-trend-grid {
  display: grid;
  gap: 0.68rem;
}

.team-trend-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.league-trend-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.team-trend-card,
.league-trend-card {
  min-width: 0;
  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 8px;
  background: rgba(3, 7, 18, 0.22);
}

.team-trend-card {
  display: grid;
  gap: 0.58rem;
  border-color: color-mix(in srgb, var(--side-color, #94a3b8) 28%, rgba(148, 163, 184, 0.14));
  background:
    linear-gradient(135deg, color-mix(in srgb, var(--side-color, #94a3b8) 12%, transparent), rgba(3, 7, 18, 0.34)),
    rgba(3, 7, 18, 0.22);
  padding: 0.68rem;
  box-shadow: inset 0 0 0 1px rgba(248, 250, 252, 0.03);
}

.team-trend-head span,
.league-trend-card-head small {
  color: var(--text-muted);
  font-size: 0.58rem;
  font-weight: 900;
  text-transform: uppercase;
}

.team-trend-head strong,
.league-trend-card-head span {
  min-width: 0;
  overflow: hidden;
  color: #f8fbff;
  font-size: 0.72rem;
  font-weight: 950;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.team-trend-market-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.45rem;
}

.team-trend-market {
  display: grid;
  justify-items: center;
  gap: 0.18rem;
  min-width: 0;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 7px;
  background:
    linear-gradient(180deg, rgba(248, 250, 252, 0.035), rgba(8, 13, 27, 0.26)),
    rgba(8, 13, 27, 0.34);
  padding: 0.44rem 0.48rem;
  text-align: center;
}

.team-trend-market--total {
  border-color: rgba(56, 189, 248, 0.25);
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.08), rgba(8, 13, 27, 0.32));
}

.team-trend-market span {
  min-width: 0;
  overflow: hidden;
  color: var(--text-muted);
  font-size: 0.56rem;
  font-weight: 900;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.team-trend-stat-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, auto));
  justify-content: center;
  gap: 0.32rem;
  width: 100%;
}

.team-trend-market strong,
.team-trend-market b {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 22px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 999px;
  padding: 0.12rem 0.36rem;
  white-space: nowrap;
}

.team-trend-market strong {
  color: #f8fbff;
  font-size: 0.82rem;
  font-weight: 950;
  line-height: 1.1;
}

.team-trend-market b {
  background: rgba(248, 250, 252, 0.08);
  color: #dbeafe;
  font-size: 0.58rem;
  font-weight: 900;
}

.team-trend-market small,
.league-trend-row small {
  min-width: 0;
  overflow: hidden;
  color: var(--text-muted);
  font-size: 0.56rem;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.league-trend-context {
  display: grid;
  gap: 0.58rem;
}

.league-trend-card {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  align-content: start;
  gap: 0.55rem;
  background:
    linear-gradient(180deg, rgba(248, 250, 252, 0.035), rgba(3, 7, 18, 0.18)),
    rgba(3, 7, 18, 0.24);
  padding: 0.6rem;
}

.league-trend-table-wrap {
  max-height: 178px;
  overflow-y: auto;
  scrollbar-color: rgba(148, 163, 184, 0.45) rgba(15, 23, 42, 0.35);
  scrollbar-width: thin;
}

.league-trend-table-wrap::-webkit-scrollbar {
  width: 7px;
}

.league-trend-table-wrap::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.35);
  border-radius: 999px;
}

.league-trend-table-wrap::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.45);
  border-radius: 999px;
}

.league-trend-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.league-trend-table th:first-child,
.league-trend-table td:first-child {
  width: 46%;
  text-align: left;
}

.league-trend-table th:not(:first-child),
.league-trend-table td:not(:first-child) {
  width: 18%;
}

.league-trend-table--total th:first-child,
.league-trend-table--total td:first-child {
  width: 30%;
}

.league-trend-table--total th:nth-child(2),
.league-trend-table--total td:nth-child(2),
.league-trend-table--total th:nth-child(3),
.league-trend-table--total td:nth-child(3) {
  width: 18%;
}

.league-trend-table--total th:nth-child(4),
.league-trend-table--total td:nth-child(4) {
  width: 34%;
}

.league-trend-table th,
.league-trend-table td {
  border-top: 1px solid rgba(148, 163, 184, 0.1);
  color: #dbeafe;
  font-size: 0.61rem;
  font-weight: 850;
  overflow: hidden;
  padding: 0.34rem 0.24rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.league-trend-table th:not(:first-child),
.league-trend-table td:not(:first-child) {
  text-align: center;
}

.league-trend-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  border-top: 0;
  background: rgba(3, 7, 18, 0.96);
  color: var(--text-muted);
  font-size: 0.53rem;
  font-weight: 950;
  text-transform: uppercase;
}

.league-trend-table td:nth-child(2),
.league-trend-table td:nth-child(3) {
  color: #f8fbff;
  font-weight: 950;
}

.odds-movement-card {
  margin-top: 1rem;
}

.odds-movement-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.72rem;
}

.odds-movement-panel {
  display: grid;
  gap: 0.62rem;
  min-width: 0;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(2, 6, 23, 0.88)),
    #07111f;
  padding: 0.68rem;
}

.odds-movement-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.6rem;
}

.odds-movement-head span {
  color: #f8fbff;
  font-size: 0.78rem;
  font-weight: 950;
}

.odds-movement-head small {
  display: block;
  color: var(--text-muted);
  font-size: 0.62rem;
  font-weight: 800;
  margin-top: 0.12rem;
}

.odds-movement-head b {
  flex: 0 0 auto;
  border: 1px solid rgba(96, 165, 250, 0.24);
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.11);
  color: #dbeafe;
  font-size: 0.58rem;
  font-weight: 900;
  line-height: 1;
  padding: 0.22rem 0.38rem;
  text-transform: uppercase;
}

.odds-movement-body {
  display: grid;
  grid-template-columns: minmax(176px, 2fr) minmax(0, 8fr);
  align-items: stretch;
  gap: 0.72rem;
  min-width: 0;
}

.odds-movement-chart {
  display: grid;
  align-items: center;
  min-width: 0;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 8px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.94), rgba(2, 6, 23, 0.82)),
    linear-gradient(180deg, rgba(96, 165, 250, 0.07), transparent 34%),
    #07111f;
  box-shadow:
    inset 0 1px 0 rgba(248, 250, 252, 0.04),
    inset 0 0 0 1px rgba(96, 165, 250, 0.035);
  padding: 0.52rem 0.5rem 0.26rem;
}

.odds-movement-book-list {
  display: grid;
  grid-template-columns: 1fr;
  align-content: start;
  gap: 0.26rem;
  max-height: 214px;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 0.16rem;
  scrollbar-color: rgba(148, 163, 184, 0.45) rgba(15, 23, 42, 0.35);
  scrollbar-width: thin;
}

.odds-movement-book {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 40px 40px 42px;
  align-items: center;
  gap: 0.2rem;
  width: 100%;
  min-width: 0;
  border: 1px solid rgba(148, 163, 184, 0.12);
  border-radius: 6px;
  background: rgba(8, 13, 27, 0.42);
  color: var(--text-muted);
  font-size: 0.52rem;
  font-weight: 850;
  padding: 0.3rem 0.32rem;
}

.odds-movement-book--header {
  position: sticky;
  top: 0;
  z-index: 1;
  border-color: rgba(148, 163, 184, 0.16);
  background: rgba(3, 7, 18, 0.94);
  color: var(--text-muted);
  text-transform: uppercase;
}

.odds-movement-book span,
.odds-movement-book strong,
.odds-movement-book b {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.odds-movement-book span {
  display: inline-flex;
  align-items: center;
  gap: 0.24rem;
  color: #dbeafe;
  font-size: 0.52rem;
}

.odds-movement-book i {
  width: 6px;
  height: 6px;
  flex: 0 0 auto;
  border-radius: 999px;
}

.odds-movement-book strong {
  color: var(--text-muted);
  font-size: 0.5rem;
  font-weight: 800;
  text-align: right;
}

.odds-movement-book b {
  color: #f8fbff;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.51rem;
  font-weight: 950;
  text-align: right;
}

.odds-movement-book--header span,
.odds-movement-book--header strong,
.odds-movement-book--header b {
  color: var(--text-muted);
  font-family: inherit;
  font-size: 0.5rem;
  font-weight: 950;
}

.odds-movement-book--up {
  color: #86efac !important;
}

.odds-movement-book--down {
  color: #fca5a5 !important;
}

.price-surface-card,
.prediction-compare-card {
  margin-top: 1rem;
}

.surface-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.surface-panel,
.comparison-row {
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(14, 165, 233, 0.09), rgba(2, 6, 23, 0.86)),
    #07111f;
  padding: 0.9rem;
}

.surface-title,
.surface-market-head,
.comparison-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
}

.surface-title {
  align-items: flex-start;
  margin-bottom: 0.8rem;
}

.surface-title h4,
.comparison-head h4 {
  font-size: 1rem;
}

.surface-title small,
.comparison-head span {
  color: var(--text-muted);
  font-size: 0.72rem;
}

.surface-title small {
  text-align: right;
}

.surface-market-list {
  display: grid;
  gap: 0.8rem;
}

.surface-market {
  display: grid;
  gap: 0.62rem;
  border: 1px solid rgba(148, 163, 184, 0.26);
  border-radius: 8px;
  background: rgba(2, 6, 23, 0.72);
  padding: 0.72rem;
}

.surface-market-head {
  align-items: flex-start;
}

.surface-market-head span {
  color: #f8fbff;
  font-weight: 850;
}

.activity-pill-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.32rem;
}

.activity-pill-row--compact,
.activity-pill-row--chart {
  justify-content: flex-end;
  margin-top: 0;
}

.activity-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 999px;
  color: #dbe4ff;
  font-size: 0.66rem;
  font-weight: 850;
  line-height: 1;
  padding: 0.28rem 0.44rem;
  white-space: nowrap;
}

.activity-pill b {
  color: var(--text-muted);
  font-size: 0.58rem;
  text-transform: uppercase;
}

.activity-pill--volume {
  background: rgba(37, 99, 235, 0.12);
  border-color: rgba(59, 130, 246, 0.34);
}

.activity-pill--oi {
  background: rgba(71, 85, 105, 0.18);
  border-color: rgba(148, 163, 184, 0.32);
}

.activity-pill--empty {
  background: rgba(71, 85, 105, 0.12);
  color: var(--text-muted);
}

.surface-market-body {
  min-width: 0;
}

.surface-pie-card {
  display: grid;
  gap: 0.68rem;
  align-content: center;
  min-width: 0;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(2, 6, 23, 0.88)),
    #07111f;
  padding: 0.72rem;
}

.surface-pie-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
}

.surface-pie-title span {
  color: #f8fbff;
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
}

.surface-pie-title small {
  color: var(--text-muted);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.68rem;
  font-weight: 800;
}

.pie-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 0.78rem;
  align-items: center;
}

.price-pie {
  position: relative;
  display: grid;
  width: 94px;
  aspect-ratio: 1;
  place-items: center;
  border-radius: 999px;
  box-shadow: inset 0 0 0 1px rgba(226, 232, 240, 0.14), 0 0 18px rgba(5, 150, 105, 0.12);
}

.surface-pie-card--polymarket .price-pie {
  box-shadow: inset 0 0 0 1px rgba(226, 232, 240, 0.14), 0 0 18px rgba(37, 99, 235, 0.12);
}

.price-pie::after {
  content: '';
  position: absolute;
  inset: 15px;
  border-radius: inherit;
  background: #08111f;
  box-shadow: inset 0 0 0 1px rgba(226, 232, 240, 0.1);
}

.pie-side {
  display: grid;
  gap: 0.24rem;
  min-width: 0;
  border: 1px solid color-mix(in srgb, var(--side-color, #94a3b8) 38%, rgba(148, 163, 184, 0.16));
  border-left: 3px solid var(--side-color, #94a3b8);
  border-radius: 7px;
  background:
    linear-gradient(180deg, var(--side-soft, rgba(148, 163, 184, 0.08)), rgba(3, 7, 18, 0.2)),
    rgba(3, 7, 18, 0.26);
  min-height: 84px;
  padding: 0.62rem;
}

.pie-side span {
  color: var(--text-muted);
  font-size: 0.66rem;
  font-weight: 850;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
}

.pie-side strong {
  color: var(--side-color, #f8fbff);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.92rem;
  text-shadow: 0 0 16px var(--side-tint, rgba(148, 163, 184, 0.16));
}

.pie-side small {
  color: #9cc9ff;
  font-size: 0.68rem;
  font-weight: 850;
}

.surface-volume-chart {
  display: grid;
  gap: 0.5rem;
  margin-top: 0.85rem;
  border-top: 1px solid rgba(202, 220, 255, 0.12);
  padding-top: 0.75rem;
}

.surface-chart-head,
.surface-chart-row {
  display: grid;
  grid-template-columns: 78px minmax(56px, 0.45fr) minmax(0, 1fr);
  gap: 0.55rem;
  align-items: center;
}

.surface-chart-head span {
  color: #f8fbff;
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
}

.surface-chart-head small {
  grid-column: 3;
  color: var(--text-muted);
  font-size: 0.66rem;
  text-align: right;
}

.surface-chart-row > span {
  color: #dbe4ff;
  font-size: 0.72rem;
  font-weight: 850;
}

.surface-chart-row small {
  color: var(--text-muted);
  font-size: 0.64rem;
  text-align: right;
  white-space: nowrap;
}

.surface-chart-bars {
  display: flex;
  align-items: end;
  gap: 0.18rem;
  height: 42px;
  border-bottom: 1px solid rgba(202, 220, 255, 0.14);
}

.surface-chart-bar {
  display: block;
  width: 13px;
  min-height: 0;
  border-radius: 4px 4px 0 0;
}

.surface-chart-bar--volume {
  background: linear-gradient(180deg, #34d399, #0f766e);
}

.surface-chart-bar--oi {
  background: linear-gradient(180deg, #93c5fd, #4f46e5);
}

.price-lane {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
}

.price-node {
  display: grid;
  gap: 0.35rem;
  min-width: 0;
  border: 1px solid rgba(202, 220, 255, 0.13);
  border-radius: 7px;
  background: rgba(3, 7, 18, 0.36);
  padding: 0.58rem;
}

.price-node-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.55rem;
}

.price-node-head span {
  color: var(--text-muted);
  font-size: 0.7rem;
  font-weight: 850;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.price-node-head strong,
.comparison-price-card strong {
  color: #e0f2fe;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.88rem;
}

.price-node small {
  color: #a7f3d0;
  font-size: 0.72rem;
  font-weight: 800;
}

.price-track {
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.18);
}

.price-track span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #38bdf8, #a78bfa);
}

.comparison-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.comparison-row {
  display: grid;
  gap: 0.66rem;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.94), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at 15% 0%, rgba(16, 185, 129, 0.18), transparent 32%),
    radial-gradient(circle at 85% 100%, rgba(248, 113, 113, 0.13), transparent 36%);
}

.comparison-head {
  align-items: flex-start;
}

.comparison-edge {
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 999px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.72rem;
  padding: 0.24rem 0.42rem;
  white-space: nowrap;
}

.comparison-edge--good {
  animation: edgePulseGreen 2.1s ease-in-out infinite;
  background: rgba(22, 163, 74, 0.15);
  border-color: rgba(74, 222, 128, 0.62);
  box-shadow: 0 0 0 1px rgba(34, 197, 94, 0.12), 0 0 18px rgba(34, 197, 94, 0.12);
  color: #bbf7d0;
}

.comparison-edge--bad {
  animation: edgePulseRed 2.1s ease-in-out infinite;
  background: rgba(220, 38, 38, 0.14);
  border-color: rgba(248, 113, 113, 0.62);
  box-shadow: 0 0 0 1px rgba(248, 113, 113, 0.12), 0 0 18px rgba(248, 113, 113, 0.12);
  color: #fecaca;
}

.comparison-edge--neutral {
  background: rgba(148, 163, 184, 0.08);
  color: var(--text-muted);
}

.comparison-delta {
  display: grid;
  grid-template-columns: minmax(0, 0.86fr) minmax(64px, 0.54fr) minmax(0, 0.86fr);
  gap: 0.48rem;
  align-items: center;
}

.comparison-price-card {
  display: grid;
  gap: 0.12rem;
  min-width: 0;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 7px;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.64), rgba(2, 6, 23, 0.4));
  padding: 0.52rem;
}

.comparison-price-card span,
.comparison-price-card small,
.comparison-row p {
  color: var(--text-muted);
  font-size: 0.68rem;
}

.comparison-price-card span {
  font-weight: 850;
  text-transform: uppercase;
}

.comparison-price-card small {
  color: #a7f3d0;
  font-weight: 800;
}

.comparison-price-card--poly small {
  color: #bfdbfe;
}

.comparison-meter {
  position: relative;
  height: 34px;
  min-width: 64px;
}

.comparison-meter__line,
.comparison-meter__zero,
.comparison-meter__node {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

.comparison-meter__line {
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(74, 222, 128, 0.82), rgba(148, 163, 184, 0.3) 50%, rgba(248, 113, 113, 0.82));
}

.comparison-meter__zero {
  left: 50%;
  width: 1px;
  height: 20px;
  background: rgba(226, 232, 240, 0.4);
}

.comparison-meter__node {
  width: 14px;
  height: 14px;
  margin-left: -7px;
  border: 2px solid #e2e8f0;
  border-radius: 999px;
  background: #020617;
  box-shadow: 0 0 18px rgba(226, 232, 240, 0.18);
}

.comparison-meter.comparison-edge--good .comparison-meter__node {
  border-color: #86efac;
  box-shadow: 0 0 18px rgba(34, 197, 94, 0.45);
}

.comparison-meter.comparison-edge--bad .comparison-meter__node {
  border-color: #fca5a5;
  box-shadow: 0 0 18px rgba(239, 68, 68, 0.45);
}

.comparison-row p {
  min-height: 1.6em;
  margin: 0;
}

@keyframes edgePulseGreen {
  0%,
  100% {
    box-shadow: 0 0 0 1px rgba(34, 197, 94, 0.12), 0 0 14px rgba(34, 197, 94, 0.1);
  }
  50% {
    box-shadow: 0 0 0 1px rgba(34, 197, 94, 0.32), 0 0 24px rgba(34, 197, 94, 0.24);
  }
}

@keyframes edgePulseRed {
  0%,
  100% {
    box-shadow: 0 0 0 1px rgba(248, 113, 113, 0.12), 0 0 14px rgba(248, 113, 113, 0.1);
  }
  50% {
    box-shadow: 0 0 0 1px rgba(248, 113, 113, 0.32), 0 0 24px rgba(248, 113, 113, 0.24);
  }
}

.market-panel {
  padding: 0.85rem;
}

.market-title {
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.market-title h4 {
  font-size: 1rem;
}

.market-title span {
  color: #a7f3d0;
  font-size: 0.78rem;
  font-weight: 800;
}

.market-stat-grid {
  display: grid;
  gap: 0.5rem;
}

.market-stat {
  display: grid;
  gap: 0.12rem;
  border-top: 1px solid rgba(202, 220, 255, 0.12);
  padding-top: 0.5rem;
}

.market-stat strong {
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.35;
}

.odds-card {
  margin-top: 1rem;
}

.best-bet-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.6rem;
  margin-bottom: 1rem;
}

.best-bet-card {
  display: grid;
  gap: 0.46rem;
  min-width: 0;
  border: 1px solid rgba(250, 204, 21, 0.42);
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(250, 204, 21, 0.12), rgba(15, 23, 42, 0.92) 42%, rgba(2, 6, 23, 0.96)),
    #07111f;
  padding: 0.72rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07), 0 10px 24px rgba(15, 23, 42, 0.08);
}

.best-bet-topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.45rem;
}

.best-bet-market {
  color: #7dd3fc;
  font-size: 0.64rem;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.best-badge {
  border: 1px solid rgba(250, 204, 21, 0.42);
  border-radius: 999px;
  background: rgba(250, 204, 21, 0.13);
  color: #fde68a;
  font-size: 0.58rem;
  font-style: normal;
  font-weight: 950;
  letter-spacing: 0;
  line-height: 1;
  padding: 0.24rem 0.38rem;
  text-transform: uppercase;
  white-space: nowrap;
}

.best-bet-card strong {
  color: #f8fbff;
  font-size: 0.78rem;
  line-height: 1.25;
  min-height: 2.45em;
  overflow-wrap: anywhere;
}

.best-bet-book {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border-top: 1px solid rgba(202, 220, 255, 0.12);
  color: var(--text-muted);
  font-size: 0.72rem;
  padding-top: 0.46rem;
}

.best-bet-book b {
  color: #a7f3d0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.82rem;
}

.odds-board-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.odds-board {
  min-width: 0;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(2, 6, 23, 0.9)),
    #07111f;
  padding: 0.85rem;
}

.odds-board-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.7rem;
}

.odds-board-head h4 {
  font-size: 1rem;
}

.odds-board-head span,
.odds-board-head small {
  color: var(--text-muted);
  font-size: 0.72rem;
}

.book-matrix {
  display: grid;
  gap: 0.42rem;
  max-height: min(430px, 54vh);
  overflow-y: auto;
  padding-right: 0.18rem;
  scrollbar-color: rgba(125, 211, 252, 0.36) rgba(15, 23, 42, 0.3);
  scrollbar-gutter: stable;
}

.book-matrix::-webkit-scrollbar {
  width: 8px;
}

.book-matrix::-webkit-scrollbar-track {
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.32);
}

.book-matrix::-webkit-scrollbar-thumb {
  border: 2px solid rgba(15, 23, 42, 0.32);
  border-radius: 999px;
  background: rgba(125, 211, 252, 0.48);
}

.book-matrix-head,
.book-row {
  display: grid;
  grid-template-columns: minmax(82px, 0.8fr) repeat(2, minmax(88px, 1fr));
  gap: 0.42rem;
  align-items: stretch;
}

.book-matrix-head {
  position: sticky;
  top: 0;
  z-index: 2;
  border-bottom: 1px solid rgba(202, 220, 255, 0.12);
  background:
    linear-gradient(180deg, rgba(25, 36, 64, 0.98), rgba(16, 24, 44, 0.96)),
    rgba(8, 13, 27, 0.94);
  color: var(--text-muted);
  font-size: 0.64rem;
  font-weight: 900;
  letter-spacing: 0;
  margin: -0.08rem 0 0.08rem;
  padding: 0.1rem 0 0.34rem;
  text-transform: uppercase;
}

.book-name,
.odds-cell {
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 7px;
  background: rgba(2, 6, 23, 0.82);
}

.book-name {
  display: flex;
  align-items: center;
  color: #dbe4ff;
  font-size: 0.78rem;
  font-weight: 800;
  padding: 0.58rem 0.62rem;
}

.odds-cell {
  display: grid;
  gap: 0.34rem;
  min-width: 0;
  padding: 0.5rem;
}

.odds-cell--best {
  border-color: rgba(45, 212, 191, 0.46);
  background:
    linear-gradient(180deg, rgba(45, 212, 191, 0.16), rgba(2, 6, 23, 0.76)),
    #07111f;
  box-shadow: inset 0 0 0 1px rgba(167, 243, 208, 0.1);
}

.odds-cell-top {
  display: flex;
  align-items: center;
  gap: 0.42rem;
}

.odds-cell-top span {
  color: var(--text-muted);
  font-size: 0.62rem;
  font-weight: 900;
  text-transform: uppercase;
}

.odds-cell-top strong {
  color: #f8fbff;
  margin-left: auto;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.84rem;
}

.odds-cell--best .odds-cell-top strong {
  color: #a7f3d0;
}

.odds-strength {
  height: 5px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.18);
}

.odds-strength span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #64748b, #38bdf8);
}

.odds-cell--best .odds-strength span {
  background: linear-gradient(90deg, #2dd4bf, #a7f3d0);
}

.cross-link {
  margin-top: 1rem;
}

.cross-link a {
  color: var(--accent);
  text-decoration: none;
}

.volume-handle-row,
.draftkings-activity-row,
.prediction-price-row,
.prediction-price-platform,
.draftkings-split-stack,
.draftkings-side-chart-card,
.team-trend-card,
.league-trend-card,
.team-trend-market,
.odds-movement-panel,
.odds-movement-chart,
.odds-movement-book,
.surface-panel,
.surface-market,
.surface-pie-card,
.pie-side,
.price-node,
.comparison-row,
.comparison-price-card,
.odds-board,
.book-name,
.odds-cell {
  border-color: rgba(148, 163, 184, 0.28);
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.97), rgba(2, 6, 23, 0.92)),
    #07111f;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.045);
}

.team-trend-market,
.comparison-price-card,
.book-name,
.odds-cell {
  background:
    linear-gradient(180deg, rgba(30, 41, 59, 0.9), rgba(2, 6, 23, 0.88)),
    #07111f;
}

.best-bet-card {
  background:
    linear-gradient(180deg, rgba(250, 204, 21, 0.12), rgba(15, 23, 42, 0.94) 42%, rgba(2, 6, 23, 0.97)),
    #07111f;
}

.surface-panel,
.comparison-row {
  background:
    linear-gradient(180deg, rgba(14, 165, 233, 0.08), rgba(15, 23, 42, 0.96) 36%, rgba(2, 6, 23, 0.94)),
    #07111f;
}

@media (max-width: 980px) {
  .matchup-board,
  .detail-grid,
  .market-grid,
  .surface-grid,
  .comparison-grid,
  .team-trend-grid,
  .league-trend-grid,
  .odds-movement-grid,
  .odds-board-grid {
    grid-template-columns: 1fr;
  }

  .best-bet-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .game-center {
    min-width: 0;
    min-height: 96px;
  }

  .line-card-groups {
    grid-template-columns: 1fr;
  }

  .line-card-group--3 .line-card-grid,
  .line-card-group--2 .line-card-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .volume-handle-grid,
  .draftkings-activity-grid {
    grid-template-columns: 1fr;
  }

  .loading-hero,
  .loading-card-grid {
    grid-template-columns: 1fr;
  }

  .odds-movement-body {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 620px) {
  .hero-topline,
  .row-head,
  .metric-pair,
  .probability-labels,
  .market-title {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.35rem;
  }

  .line-card-group--3 .line-card-grid,
  .line-card-group--2 .line-card-grid {
    grid-template-columns: 1fr;
  }

  .best-bet-grid,
  .book-row,
  .draftkings-side-chart-grid,
  .prediction-price-details,
  .prediction-price-platform-grid,
  .team-trend-market-grid,
  .odds-movement-book-list,
  .surface-chart-head,
  .surface-chart-row,
  .surface-market-body,
  .price-lane {
    grid-template-columns: 1fr;
  }

  .surface-chart-head small,
  .surface-chart-row small {
    text-align: left;
  }

  .book-matrix-head {
    display: none;
  }

  .odds-movement-book {
    grid-template-columns: minmax(0, 1fr) 42px 42px 44px;
    gap: 0.24rem;
    padding: 0.3rem 0.36rem;
  }

  .odds-movement-book span,
  .odds-movement-book strong,
  .odds-movement-book b {
    font-size: 0.53rem;
  }

  .surface-title,
  .surface-market-head,
  .comparison-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.35rem;
  }

  .comparison-delta {
    grid-template-columns: 1fr;
  }

  .team-panel {
    min-height: 132px;
  }
}
</style>
